import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { publicProductService, publicCategoryService } from '../services/publicApi';
import { setPageJsonLd, buildBreadcrumbJsonLd } from '../utils/seoDom';
import './Products.css';

const SITE_URL = 'https://www.domoelec.ma';
const DEBUG_MODE = false;
/** Panneau latéral : afficher recherche + Catégorie / Sous-catégorie / Sous-sous-catégorie. */
const SHOW_SIDEBAR_SEARCH_AND_CATEGORY = true;
const Dbg = {
  log:   (...a) => { if (DEBUG_MODE) try { console.log(...a);   } catch {} },
  table: (...a) => { if (DEBUG_MODE) try { console.table(...a); } catch {} },
  group: (...a) => { if (DEBUG_MODE) try { console.group(...a); } catch {} },
  groupEnd: ()   => { if (DEBUG_MODE) try { console.groupEnd(); } catch {} },
  warn:  (...a) => { if (DEBUG_MODE) try { console.warn(...a);  } catch {} },
};

// Material UI
import {
  Row, Col, Card as AntCard, Typography as AntTypography, Input, Switch,
  Select as AntSelect, Button as AntButton, Tag, Pagination as AntPagination,
  Space, Popover, Drawer, Badge, Divider, Collapse, AutoComplete, Tree as AntTree, Checkbox
} from 'antd';
import SimplePagination from '../components/SimplePagination';
import {
  SearchOutlined, FilterOutlined, ClearOutlined,
  DownOutlined, CloseOutlined, ShopOutlined
} from '@ant-design/icons';

const normalizeName = (n) => String(n || '').trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');

// « VIDÉOSURVEILLANCE » → « Vidéosurveillance » — les noms Excel en
// majuscules deviennent lisibles ; les noms en casse mixte sont conservés.
const toDisplayName = (s) => {
  const t = String(s || '').trim();
  if (!t || t !== t.toUpperCase()) return t;
  return t.toLowerCase().replace(/(^|\s|\/|-)\S/g, c => c.toUpperCase());
};

const FAMILY_ICON_MAP = {
  'detection incendie': '🔥',
  'détection incendie': '🔥',
  'alarme incendie': '🚨',
  'controle d acces': '🚪',
  'contrôle d accès': '🚪',
  'controle d\'acces': '🚪',
  'contrôle d\'accès': '🚪',
  'videosurveillance': '📹',
  'reseaux infrastructure': '🔌',
  'réseaux infrastructure': '🔌',
  'reseaux & infrastructure': '🔌',
  'réseaux & infrastructure': '🔌',
  'equipements divers': '⚙️',
  'équipements divers': '⚙️',
  'onduleurs': '🔋',
  'domotique': '🏠',
  'climatisation': '❄️',
  'electricite': '⚡',
  'électricité': '⚡',
  'cablage': '🔗',
  'câblage': '🔗',
  'securite': '🛡️',
  'sécurité': '🛡️',
};
const getFamilyIcon = (name) => {
  const n = normalizeName(name);
  const hit = Object.keys(FAMILY_ICON_MAP).find(k => normalizeName(k) === n);
  if (hit) return FAMILY_ICON_MAP[hit];
  return '📦';
};

const recoverFrenchText = (raw) => {
  if (raw == null) return raw;
  if (typeof raw !== 'string') return raw;
  if (raw.length === 0) return raw;
  if (!raw.includes('\uFFFD') && !/[ÃÂÄÆæŒœ€]/.test(raw)) return raw;

  let s = raw;

  // --- Niveau 1 : Mojibake (UTF-8 stocké en Latin-1) ---
  if (/[ÃÂÄÆæŒœ€]/.test(s)) {
    try {
      const bytes = new Uint8Array(s.length);
      for (let i = 0; i < s.length; i++) {
        bytes[i] = s.charCodeAt(i) & 0xff;
      }
      const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
      const nOld = (s.match(/\uFFFD/g) || []).length;
      const nNew = (decoded.match(/\uFFFD/g) || []).length;
      if (!decoded.includes('\uFFFD') || nNew < nOld) s = decoded;
    } catch (_) {}
  }

  if (!s.includes('\uFFFD')) return s;

  // --- Niveau 2 : Dictionnaire de mots français (U+FFFD → accent correct) ---
  const DICT = [
    [/monophas\uFFFD/gi, 'monophasé'],
    [/triphas\uFFFD/gi, 'triphasé'],
    [/phas\uFFFD/gi, 'phasé'],
    [/r\uFFFDseau/gi, 'réseau'],
    [/r\uFFFDgl(e|er|es|ement|ages|age|ez|era|erai|erons|erez|aient|ant)/gi, (m, p) => 'régl' + p],
    [/r\uFFFDsult(at|ats|er|era|erai|erons|erez)/gi, (m, p) => 'résult' + p],
    [/r\uFFFDserv(e|er|es|ation|ez|era|erai|erons|erez)/gi, (m, p) => 'réserv' + p],
    [/r\uFFFDunion(s)?/gi, (m, p) => 'réunion' + (p || '')],
    [/r\uFFFDduis(ant|ance|re|ez|era|erai|erons|erez)/gi, (m, p) => 'réduis' + p],
    [/r\uFFFDcup(e|er|ere|erez|erai|erons|erez|ération|érations)/gi, (m, p) => 'récup' + p],
    [/r\uFFFDf\uFFFDrenc(e|es|er|ez|era|erai|erons|erez)/gi, (m, p) => 'référenc' + p],
    [/r\uFFFDception(s)?/gi, (m, p) => 'réception' + (p || '')],
    [/r\uFFFDceptionn(e|er|ez|era|erai|erons|erez)/gi, (m, p) => 'réceptionn' + p],
    [/r\uFFFDact(e|eur|eurs|er|ez|if|ifs|ive|ives|era|erai|erons|erez)/gi, (m, p) => 'réact' + p],
    [/r\uFFFDel(le|lles|s)?/gi, (m, p) => 'réel' + (p || '')],
    [/r\uFFFDgion(s)?/gi, (m, p) => 'région' + (p || '')],
    [/r\uFFFDgime(s)?/gi, (m, p) => 'régime' + (p || '')],
    [/r\uFFFDpar(e|er|ez|era|erai|erons|erez|ation|ations)/gi, (m, p) => 'répar' + p],
    [/r\uFFFDp\uFFFDt(e|er|ez|era|erai|erons|erez|ition|itions)/gi, (m, p) => 'répét' + p],
    [/r\uFFFDponse(s)?/gi, (m, p) => 'réponse' + (p || '')],
    [/r\uFFFDpond(re|ez|era|erai|erons|erez|ant|er)/gi, (m, p) => 'répond' + p],
    [/r\uFFFDussi(e|te|tes|r|re|ez|era|erai|erons|erez|ant)/gi, (m, p) => 'réussi' + p],
    [/r\uFFFDsidu(e|el|els|elle|elles|s)?/gi, (m, p) => 'résidu' + (p || '')],
    [/r\uFFFDsist(ance|antes?|ant|er|ez|era|erai|erons|erez)/gi, (m, p) => 'résist' + p],
    [/r\uFFFDsolu(e|s|tion|tions)?/gi, (m, p) => 'résolu' + (p || '')],
    [/r\uFFFDv(e|er|ez|es|ement|era|erai|erons|erez|ision|isions)/gi, (m, p) => 'rêv' + p],
    [/r\uFFFDveil(s)?/gi, (m, p) => 'réveil' + (p || '')],
    [/r\uFFFDveill(e|er|ez|era|erai|erons|erez|ant)/gi, (m, p) => 'réveill' + p],
    [/r\uFFFDvol(t|ter|ez|era|erai|erons|erez|ution|utions)/gi, (m, p) => 'révol' + p],

    [/t\uFFFDl\uFFFD(phone|charger|cope|path|com|gramme|marketing|communication|surveillance|vision|scopie|grammes|phones|viseur|viseurs|faisceau|faisceaux|cran|crans)/gi, (m, p) => 'télé' + p],
    [/t\uFFFDl(é|e)?/gi, (m) => m[0] === m[0].toUpperCase() ? 'Tél' : 'tél'],
    [/t\uFFFDmoin(s)?/gi, (m, p) => 'témoin' + (p || '')],
    [/t\uFFFDl\uFFFDcommande(s)?/gi, (m, p) => 'télécommande' + (p || '')],
    [/t\uFFFDl\uFFFDphoni(e|que|ques|er|ez|era|erai|erons|erez)/gi, (m, p) => 'téléphoni' + p],
    [/t\uFFFDl\uFFFDvision(s)?/gi, (m, p) => 'télévision' + (p || '')],
    [/t\uFFFDl\uFFFDsurveillance/gi, 'télésurveillance'],

    [/\uFFFDlect(rique|ronique|rification|rificat|eur|eurs|roménager|ro|ive|r|ries|rie|eur|eurs|rodomestique|rodomestiques|risabilit|risables?)/gi, (m, p) => 'élect' + p],
    [/\uFFFDnerg(ie|étique|étiques|ys|ies|étique|tique|tiques)/gi, (m, p) => 'énerg' + p],
    [/\uFFFDcran(s)?/gi, (m, p) => 'écran' + (p || '')],
    [/\uFFFDcoute(s|ur|urs|rice|rices|z|aient|ait|ant|er|ez|era|erai|erons|erez)?/gi, (m, p) => 'écoute' + (p || '')],
    [/\uFFFDcole(s)?/gi, (m, p) => 'école' + (p || '')],
    [/\uFFFDconom(ie|ique|iques|iquement|istes?|iste)/gi, (m, p) => 'économ' + p],
    [/\uFFFDgal(iser|ement|ité|e|is|isations|isation|s|ez|era|erai|erons|erez|ant)/gi, (m, p) => 'égal' + p],
    [/\uFFFDviter/gi, 'éviter'],
    [/\uFFFDvit(e|ez|era|erai|erons|erez|ant|es)/gi, (m, p) => 'évit' + p],
    [/\uFFFDv\uFFFDnement(s)?/gi, (m, p) => 'événement' + (p || '')],
    [/\uFFFDquip(e|er|ez|era|erai|erons|erez|ant|ement|ements)/gi, (m, p) => 'équip' + p],
    [/\uFFFDquipement(s)?/gi, (m, p) => 'équipement' + (p || '')],
    [/\uFFFDtend(ue|ues|u|re|res|ez|era|erai|erons|erez|ant|re)?/gi, (m, p) => 'étend' + (p || '')],
    [/\uFFFDtage(s)?/gi, (m, p) => 'étage' + (p || '')],
    [/(?<![a-zA-Z])\uFFFDt(e|es|ait|aient|ant|ez|era|erai|erons|erez|re|r|uve|uves|é|e)/gi, (m, p) => 'ét' + p],
    [/\uFFFDtabl(e|ir|it|issons|issez|issent|issement|issements|eau|eaux)/gi, (m, p) => 'établ' + p],
    [/\uFFFDtanch(e|es|éit|éité|ement|ements)/gi, (m, p) => 'étanch' + p],
    [/\uFFFDtat(s)?/gi, (m, p) => 'état' + (p || '')],
    [/\uFFFDtudiant(s)?/gi, (m, p) => 'étudiant' + (p || '')],
    [/\uFFFDtude(s)?/gi, (m, p) => 'étude' + (p || '')],
    [/\uFFFDvanou(ir|it|issons|issez|issent|issant|ie)/gi, (m, p) => 'évanou' + p],
    [/\uFFFDveil(l|le|les|ler|lez|lera|lerai|lerons|lerez|lait|laient|lant)/gi, (m, p) => 'éveil' + p],
    [/\uFFFDvident(e|es)?/gi, (m, p) => 'évident' + (p || '')],
    [/\uFFFDvou\uFFFD/gi, 'évoué'],
    [/\uFFFDpaule(s)?/gi, (m, p) => 'épaule' + (p || '')],
    [/\uFFFDpais(e|ses|sse|sses)/gi, (m, p) => 'épais' + p],
    [/\uFFFDpaisseur(s)?/gi, (m, p) => 'épaisseur' + (p || '')],
    [/\uFFFDpous(e|er|ez|era|erai|erons|erez|ant|es)/gi, (m, p) => 'épous' + p],
    [/\uFFFDpoxy/gi, 'époxy'],
    [/\uFFFDpreuve(s)?/gi, (m, p) => 'épreuve' + (p || '')],
    [/\uFFFDpurer/gi, 'épurer'],
    [/\uFFFDpur(e|es|er|ez|era|erai|erons|erez|ant)/gi, (m, p) => 'épur' + p],

    [/acc\uFFFDes(s|ible|ibilité|oires)?/gi, (m, p) => 'accès' + (p || '')],
    [/capacit\uFFFD(s)?/gi, (m, p) => 'capacité' + (p || '')],
    [/qualit\uFFFD(s)?/gi, (m, p) => 'qualité' + (p || '')],
    [/num\uFFFDro(s)?/gi, (m, p) => 'numéro' + (p || '')],
    [/cat\uFFFDgorie(s)?/gi, (m, p) => 'catégorie' + (p || '')],
    [/d\uFFFDfin(ition|itive|ir|iss|ie|is|issent|issons|issez|i)/gi, (m, p) => 'défin' + p],
    [/d\uFFFDpense(s)?/gi, (m, p) => 'dépense' + (p || '')],
    [/d\uFFFDtection(s)?/gi, (m, p) => 'détection' + (p || '')],
    [/d\uFFFDt\uFFFDct(e|eur|eurs|er|ez|era|erai|erons|erez|if|ifs|ive|ives)/gi, (m, p) => 'détect' + p],
    [/d\uFFFDclenche(r|ez|era|erai|erons|erez|ant|ement|ements|eur|eurs)/gi, (m, p) => 'déclenche' + p],
    [/d\uFFFDclaration(s)?/gi, (m, p) => 'déclaration' + (p || '')],
    [/d\uFFFDfaut(s)?/gi, (m, p) => 'défaut' + (p || '')],
    [/d\uFFFDfavoris(e|er|ez|era|erai|erons|erez)/gi, (m, p) => 'défavoris' + p],
    [/d\uFFFDgr\uFFFD(e|s|er|ez|era|erai|erons|erez|ant|ation|ations)/gi, (m, p) => 'dégrad' + p],
    [/d\uFFFDmar(r|ch|age|er|e|ez|era|erai|erons|erez|ant)/gi, (m, p) => 'démar' + p],
    [/d\uFFFDpann(e|er|ez|era|erai|erons|erez|age|ages)/gi, (m, p) => 'dépann' + p],
    [/d\uFFFDploi(e|er|ez|era|erai|erons|erez|ant|ement|ements)/gi, (m, p) => 'déploi' + p],
    [/d\uFFFDport(e|er|ez|era|erai|erons|erez|ant|s)?/gi, (m, p) => 'déport' + p],
    [/d\uFFFDr(ive|ives|iver|ez|era|erai|erons|erez|ant|é|e|es|ait|aient)/gi, (m, p) => 'dériv' + p],
    [/d\uFFFDesir(e|er|ez|era|erai|erons|erez|ant|eux|euses)/gi, (m, p) => 'désir' + p],
    [/d\uFFFDsserv(ir|it|ice|ices|ez|ira|irai|irons|irez|issant)/gi, (m, p) => 'désserv' + p],
    [/d\uFFFDtail(s)?/gi, (m, p) => 'détail' + (p || '')],
    [/d\uFFFDvelopp(e|er|ez|era|erai|erons|erez|ant|ement|ements)/gi, (m, p) => 'développ' + p],
    [/d\uFFFDverrouill(e|er|ez|era|erai|erons|erez|age|ages)/gi, (m, p) => 'déverrouill' + p],
    [/d\uFFFDviss(e|er|ez|era|erai|erons|erez|age|ages)/gi, (m, p) => 'déviss' + p],

    [/v\uFFFDrifi(er|ez|era|erai|erons|erez|ant|ation|ations|e|es|ait|aient|if|ifs|ive|ives)/gi, (m, p) => 'vérifi' + p],
    [/v\uFFFDg\uFFFDi(ation|al|aux|er|ez|era|erai|erons|erez)/gi, (m, p) => 'végét' + p],
    [/v\uFFFDhicule(s)?/gi, (m, p) => 'véhicule' + (p || '')],
    [/v\uFFFDlo(s)?/gi, (m, p) => 'vélo' + (p || '')],
    [/v\uFFFDr(ité|it|es|er|ez|era|erai|erons|erez|itable|itables|ituellement)/gi, (m, p) => 'vér' + p],
    [/v\uFFFDtu(e|s|re|res|ement|ements)/gi, (m, p) => 'vêtu' + p],

    [/s\uFFFDcurit\uFFFD(s)?/gi, (m, p) => 'sécurité' + (p || '')],
    [/s\uFFFDlection(s)?/gi, (m, p) => 'sélection' + (p || '')],
    [/s\uFFFDrvice(s)?/gi, (m, p) => 'service' + (p || '')],
    [/s\uFFFDpar(e|er|ez|era|erai|erons|erez|ant|ation|ations|e|es|ement|ements)/gi, (m, p) => 'sépar' + p],
    [/s\uFFFDquence(s)?/gi, (m, p) => 'séquence' + (p || '')],
    [/s\uFFFDie(s)?/gi, (m, p) => 'série' + (p || '')],
    [/s\uFFFDieux/gi, 'sérieux'],

    [/p\uFFFDrim(e|er|es|ent|ère|es|ez|era|erai|erons|erez|ant|ètre|ètres)/gi, (m, p) => 'prem' + p],
    [/p\uFFFDre(s)?/gi, (m, p) => 'père' + (p || '')],
    [/p\uFFFDriph(érique|ériques|erie|eries)/gi, (m, p) => 'périph' + p],
    [/p\uFFFDriod(e|es|ique|iques)/gi, (m, p) => 'périod' + p],
    [/p\uFFFDrim\uFFFDtre(s)?/gi, (m, p) => 'périmètre' + (p || '')],
    [/p\uFFFDnal(t|ty|ties)/gi, (m, p) => 'pénal' + p],
    [/p\uFFFDch(e|er|ez|era|erai|erons|erez|ant|eaux)/gi, (m, p) => 'pêch' + p],
    [/p\uFFFDse(s|ur|urs|e|z|aient|ait|ant|er|ez|era|erai|erons|erez|age|ages)?/gi, (m, p) => 'pèse' + (p || '')],

    [/c\uFFFDbl(er|age|e|es|ages|é|ez|era|erai|erons|erez|ant)/gi, (m, p) => 'câbl' + p],
    [/c\uFFFDble(s)?/gi, (m, p) => 'câble' + (p || '')],
    [/c\uFFFDi\uFFFD(s)?/gi, (m, p) => 'côté' + (p || '')],
    [/c\uFFFDne(s)?/gi, (m, p) => 'cône' + (p || '')],
    [/c\uFFFDdre(s)?/gi, (m, p) => 'cèdre' + (p || '')],
    [/c\uFFFDd(e|er|ez|era|erai|erons|erez|ant|re|r)/gi, (m, p) => 'cèd' + p],
    [/c\uFFFDl(ebre|ebres|èbre|èbres|errer|ez|errera|errerai|errerons|errerez|ant|e|es|er)/gi, (m, p) => 'cél' + p],
    [/c\uFFFDr(e|er|ez|era|erai|erons|erez|ant|eaux|es)/gi, (m, p) => 'cér' + p],
    [/c\uFFFDram(ique|iques|ie)/gi, (m, p) => 'céram' + p],

    [/f\uFFFDlix/gi, 'félix'],
    [/f\uFFFDvrier/gi, 'février'],
    [/f\uFFFDer(e|ez|era|erai|erons|erez|ait|aient|ant|ies|i)/gi, (m, p) => 'féer' + p],
    [/f\uFFFDe/gi, 'fée'],
    [/f\uFFFDch(e|er|ez|era|erai|erons|erez|ant|eaux|es)/gi, (m, p) => 'fêch' + p],
    [/f\uFFFDte(s)?/gi, (m, p) => 'fête' + (p || '')],
    [/f\uFFFDi(e|es|ier|iers|ent|ait|aient)/gi, (m, p) => 'fêt' + p],
    [/f\uFFFDbr(ique|iques|iquer|ez|era|erai|erons|erez|ant|il|ille|illes)/gi, (m, p) => 'fébr' + p],

    [/g\uFFFDi(e|er|ez|era|erai|erons|erez|ant|eaux|es|ial|iale|iaux|ie)/gi, (m, p) => 'gén' + p],
    [/g\uFFFDiial(e|s)?/gi, (m, p) => 'génial' + p],
    [/g\uFFFDie(s)?/gi, (m, p) => 'génie' + (p || '')],
    [/g\uFFFDol(ogie|ogique|ogiques|ogiste|ogistes)/gi, (m, p) => 'géol' + p],
    [/g\uFFFDom(etrie|étrie|étriques|etrique|étrique|etriques)/gi, (m, p) => 'géom' + p],
    [/g\uFFFDran(t|te|tes|tir|ez|tira|tirai|tirons|tirez|tissant)/gi, (m, p) => 'géran' + p],
    [/g\uFFFDr(d|der|ez|dera|derai|derons|derez|dant|e|es)/gi, (m, p) => 'gér' + p],
    [/g\uFFFDte(s)?/gi, (m, p) => 'gîte' + (p || '')],

    [/h\uFFFDeberg(e|er|ez|era|erai|erons|erez|ant|eaux|es|ement|ements)/gi, (m, p) => 'héberg' + p],
    [/h\uFFFDeros/gi, 'héros'],
    [/h\uFFFDi(e|er|ez|era|erai|erons|erez|ant|eaux|es|ellerie|ellories)/gi, (m, p) => 'hêt' + p],
    [/h\uFFFDl(ice|ices|iciel|iciels|ico|iques)/gi, (m, p) => 'hél' + p],

    [/j\uFFFDr(ie|ies|idique|idiques|uriste|istes)/gi, (m, p) => 'jur' + p],

    [/l\uFFFDg(ende|endes|endaire|endaires|er|ez|era|erai|erons|erez|ant)/gi, (m, p) => 'lég' + p],
    [/l\uFFFDve(s|r|rs|ment|ments|e|z|aient|ait|ant|er|ez|era|erai|erons|erez)?/gi, (m, p) => 'lève' + (p || '')],
    [/l\uFFFDg(e|er|ez|era|erai|erons|erez|ant|ers?|ere|eres)/gi, (m, p) => 'lég' + p],
    [/l\uFFFDser/gi, 'léser'],
    [/l\uFFFDes(e|er|ez|era|erai|erons|erez|ant|ion|ions)/gi, (m, p) => 'lés' + p],

    [/m\uFFFDdical(e|s)?/gi, (m, p) => 'médical' + p],
    [/m\uFFFDditerran(ée|éen|éenne|éens|éennes)/gi, (m, p) => 'méditerran' + p],
    [/m\uFFFDdiocre/gi, 'médiocre'],
    [/m\uFFFDdium(s)?/gi, (m, p) => 'médium' + (p || '')],
    [/m\uFFFDdi(e|at|ation|ations|ev|eval|evaux|e|es|er|ez|era|erai|erons|erez)/gi, (m, p) => 'médi' + p],
    [/m\uFFFDmor(ie|ies|iser|ise|ises|ez|era|erai|erons|erez|ant)/gi, (m, p) => 'mémor' + p],
    [/m\uFFFDme(s)?/gi, (m, p) => 'même' + (p || '')],
    [/m\uFFFDdecin(e|es)/gi, (m, p) => 'médecin' + p],

    [/n\uFFFDc(essaire|essaires|essaire|essaires|esse|esses)/gi, (m, p) => 'nécess' + p],
    [/n\uFFFDg(atif|atives|atife|atives|ation|ations|atier|atriers|er|ez|era|erai|erons|erez|ant|lige|liges)/gi, (m, p) => 'nég' + p],
    [/n\uFFFDgoc(e|ier|ez|era|erai|erons|erez|iant|iation|iations)/gi, (m, p) => 'négoc' + p],
    [/n\uFFFDo(e|n|nat|nate|nates|phrite|phrites|plasme|plasmes)/gi, (m, p) => 'néo' + p],
    [/n\uFFFDr(e|er|ez|era|erai|erons|erez|ant|e|es|esse|esses|euse|euses)/gi, (m, p) => 'nér' + p],

    // — ê (lettres avant êt/êche/êle) —
    [/pr\uFFFDt(e|s|er|era|erai|erons|erez|ant|é|ée|ées)?/gi, (m, p) => 'prêt' + (p || '')],
    [/arr\uFFFDt(e|s|er|era|erai|erons|erez|ant|é|ée|ées)?/gi, (m, p) => 'arrêt' + (p || '')],
    [/t\uFFFDte(s)?/gi, (m, p) => 'tête' + (p || '')],
    [/t\uFFFDtu(e|s)?/gi, (m, p) => 'têtu' + (p || '')],
    [/b\uFFFDte(s)?/gi, (m, p) => 'bête' + (p || '')],
    [/b\uFFFDche(s)?/gi, (m, p) => 'bêche' + (p || '')],
    [/b\uFFFDler/gi, 'bêler'],
    [/cr\uFFFDte(s)?/gi, (m, p) => 'crête' + (p || '')],
    [/cr\uFFFDpe(s)?/gi, (m, p) => 'crêpe' + (p || '')],
    [/m\uFFFDler/gi, 'mêler'],
    [/m\uFFFDl(é|ée|ées|e|es)?/gi, (m, p) => 'mêl' + (p || '')],
    [/f\uFFFDler/gi, 'fêler'],
    [/f\uFFFDlure(s)?/gi, (m, p) => 'fêlure' + (p || '')],
    [/for\uFFFDt(s)?/gi, (m, p) => 'forêt' + (p || '')],

    // — è —
    [/a\uFFFD/g, 'à'],
    [/d\uFFFDes/gi, 'dès'],
    [/d\uFFFDj /gi, 'déj '],
    [/d\uFFFDj\uFFFD/gi, 'déjà'],
    [/d\uFFFDesormais/gi, 'désormais'],
    [/d\uFFFDshabiller/gi, 'déshabiller'],
    [/d\uFFFDshidrat(e|ion|ions|er|ez|era|erai|erons|erez|ant)/gi, (m, p) => 'déshidrat' + p],
    [/d\uFFFDesagr(e|able|ables|er|ez|era|erai|erons|erez|ant)/gi, (m, p) => 'désagr' + p],
    [/cr\uFFFD/gi, 'cré'],
    [/cr\uFFFD\uFFFD/gi, 'créé'],
    [/cr\uFFFDe(s|z|nt|rais|rait|raient|riez|rions|rurent|ut|utes|us|use|uses|ant)?/gi, (m, p) => 'cré' + (p || '')],
    [/entr\uFFFDe(s)?/gi, (m, p) => 'entrée' + (p || '')],
    [/for\uFFFDi(s)?/gi, (m, p) => 'forêt' + (p || '')],
    [/p\uFFFDche(s|r|er|ez|era|erai|erons|erez|ant|erie|eries|eur|eurs|eaux|e|es)?/gi, (m, p) => 'pêche' + (p || '')],

    // — à —
    [/l\uFFFD/gi, 'là'],
    [/d\uFFFD/gi, 'de'],
    [/o\uFFFD/gi, 'où'],

    // — ç —
    [/fa\uFFFDon(s)?/gi, (m, p) => 'façon' + (p || '')],
    [/gar\uFFFDon(s)?/gi, (m, p) => 'garçon' + (p || '')],
    [/le\uFFFDon(s)?/gi, (m, p) => 'leçon' + (p || '')],
    [/ma\uFFFDon(s)?/gi, (m, p) => 'maçon' + (p || '')],

    // — œ —
    [/cœur(s)?/gi, (m, p) => 'cœur' + (p || '')],
    [/c\uFFFDur(s)?/gi, (m, p) => 'cœur' + (p || '')],
    [/sœur(s)?/gi, (m, p) => 'sœur' + (p || '')],
    [/s\uFFFDur(s)?/gi, (m, p) => 'sœur' + (p || '')],
    [/\uFFFDouf(s)?/gi, (m, p) => 'œuf' + (p || '')],
    [/\uFFFDouvre(s)?/gi, (m, p) => 'œuvre' + (p || '')],
    [/\uFFFDil(s)?/gi, (m, p) => 'œil' + (p || '')],
    [/fr\uFFFDequent(e|s|er|ez|era|erai|erons|erez|ant|ation|ations)/gi, (m, p) => 'fréquent' + p],

    // — ù / û —
    [/connu/gi, 'connu'],
    [/re\uFFFDu/gi, 'reçu'],
    [/re\uFFFD(e|s|ue|ues)/gi, (m, p) => 'reç' + p],

    // — î / ï —
    [/\uFFFDle(s)?/gi, (m, p) => 'île' + (p || '')],
    [/ma\uFFFDi(i?tre(s)?)/gi, (m, p) => 'maî' + p],
    [/th\uFFFD(me|mes|orie|ories|orique|oriques)/gi, (m, p) => 'thé' + p],
    [/na\uFFFDF(ve|ves|f|ves)?/gi, (m, p) => 'naïf' + (p || '')],
    [/ma\uFFFDon(s)?/gi, (m, p) => 'maison' + (p || '')],
    [/raison(s)?/gi, (m, p) => 'raison' + (p || '')],

    // — Expressions courantes —
    [/gestion r\uFFFD/gi, 'gestion ré'],
    [/\uFFFD /g, 'é '],
    [/ \uFFFD/g, ' é'],
    [/\uFFFD-/g, 'é-'],
    [/-\uFFFD/g, '-é'],
  ];

  for (let i = 0; i < DICT.length; i++) {
    try {
      const newS = s.replace(DICT[i][0], DICT[i][1]);
      if (newS !== s) s = newS;
    } catch (_) {}
  }

  // --- Niveau 3 : Fallback contextuel pour les � restants ---
  if (s.includes('\uFFFD')) {
    s = s.replace(/ \uFFFD /g, ' à ');
    s = s.replace(/^\uFFFD /g, 'À ');
    s = s.replace(/ \uFFFD$/g, ' à');
    s = s.replace(/ \uFFFD(?=[.!?,;:])/g, ' à');
    s = s.replace(/ \uFFFD(?=')/g, ' à');

    s = s.replace(/\bd\uFFFD\b/gi, m => m[0] === 'D' ? 'Dé' : 'dé');

    s = s.replace(/([a-zA-Z])\uFFFD\b/g, (m, prev) => {
      const p = String(prev);
      const isUpper = m[0] === m[0].toUpperCase();
      const accent = (p === 'c') ? 'ç' : 'é';
      return p + (isUpper && p === p.toUpperCase() ? accent.toUpperCase() : accent);
    });

    s = s.replace(/\uFFFD([a-z])/gi, (m, nxt) => {
      const cap = m[0] === m[0].toUpperCase();
      return (cap ? 'É' : 'é') + nxt;
    });

    s = s.replace(/([a-zA-Z])\uFFFD([a-zA-Z])/g, (m, prev, nxt) => prev + 'é' + nxt);

    s = s.replace(/\uFFFD/g, 'é');
  }

  return s;
};

const deepRecover = (val) => {
  if (val == null) return val;
  if (typeof val === 'string') return recoverFrenchText(val);
  if (typeof val !== 'object') return val;
  if (Array.isArray(val)) return val.map(deepRecover);
  const out = {};
  for (const k of Object.keys(val)) {
    const rk = recoverFrenchText(k);
    out[rk] = deepRecover(val[k]);
  }
  return out;
};

const CLEAN_DISPLAY_REPAIR_MAP = (() => {
  const FFD = '\uFFFD';
  const P = (pieces) => pieces.join(FFD);
  const R = (s, flags) => new RegExp(s, flags);
  const pairs = [
    [P(['D','tect']),       'Détect'],
    [P(['d','tect']),       'détect'],
    [P(['monophas']),       'monophasé'],
    [P(['triphas']),        'triphasé'],
    [P(['r','f','rence']),  'référence'],
    [P(['R','f','rence']),  'Référence'],
    [P(['d','crit']),       'décrit'],
    [P(['','quip']),        'équip'],
    [P(['','tabli']),       'établi'],
    [P(['','tabl']),        'établ'],
    [P(['s','curit']),      'sécurité'],
    [P(['S','curit']),      'Sécurité'],
    [P(['gr','ce']),        'grâce'],
    [P(['','nergie']),      'énergie'],
    [P(['','lectrique']),   'électrique'],
    [P(['','lectron']),     'électron'],
    [P(['fum','e']),        'fumée'],
    [P(['Fum','e']),        'Fumée'],
    [P(['qualit']),         'qualité'],
    [P(['autorit']),        'autorité'],
    [P(['optiqu','e']),     'optique'],
    [P(['Optiqu','e']),     'Optique'],
    [P(['ponctuel']),       'ponctuel'],
    [P(['Ponctuel']),       'Ponctuel'],
    [P(['Gamme']),          'Gamme'],
    [P(['Onduleur']),       'Onduleur'],
    [P(['onduleur']),       'onduleur'],
    [P(['Adressable']),     'Adressable'],
    [P(['adressable']),     'adressable']
  ];
  return pairs.map(([p, r]) => [R(p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), r]);
})();

const cleanDisplay = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  if (Array.isArray(v)) return v.map(x => cleanDisplay(x));
  if (typeof v === 'object') {
    const out = {};
    for (const k of Object.keys(v)) out[k] = cleanDisplay(v[k]);
    return out;
  }
  let s = String(v);
  if (/\uFFFD/.test(s)) {
    for (const [rx, replacement] of CLEAN_DISPLAY_REPAIR_MAP) {
      s = s.replace(rx, replacement);
    }
  }
  s = s.replace(/\uFFFD{2,}/g, ' · ');
  s = s.replace(/\uFFFD/g, '');
  s = s.replace(/\s+/g, ' ').trim();
  return s;
};

const KNOWN_RESERVED_URL_PARAMS = new Set([
  'search', 'category', 'page', 'pageSize', 'exact', 'sortBy', 'sortOrder', 'inStock', 'maxPrice'
]);

// Clés qui NE SONT JAMAIS des critères personnalisés (filters UI facet mais pas fcN_name/fcN_value)
const FORBIDDEN_GLOBAL_AS_FC = new Set([
  ...KNOWN_RESERVED_URL_PARAMS,
  'brand','marque','system','systeme','gastype','typedegas','gasdetectortype',
  'detectortype','typedetecteur','usage','utilisation','connectivity','connectivite',
  'norme','certification','certifie','solution','equipement','subcategory','souscategorie',
  'categorie','category','type','appareil','resolution','technologie','techno','debit',
  'fonctionnalite','fonction','puissance','phase','phases','monophase','triphase','tension',
  'courant','frequence','protection','indice','ip','garantie','materiau','couleur','montage',
  'installation','rendement','capacite','autonomie','origine','compatibilite','interface','protocole',
  'wifi','zigbee','zwave','bluetooth','modbus','rs485','knx','bacnet','famille','status','isvisible'
]);
const RESERVED_CATEGORY_LABELS = new Set([
  'ALIMENTATION',
]);
const VIRTUAL_FACET_PARENT_LABELS = new Set([
  'MARQUE',
  'CATEGORIE',
  'CATEGORY',
  'PHASE',
  'PUISSANCE',
  'CONNEXION',
  'CONNECTIVITE',
  'CONNECTIVITÉ',
  'USAGE',
  'SOLUTION',
  'NORME',
  'EQUIPEMENT',
  'ÉQUIPEMENT',
  'TECHNOLOGIE',
  'RESOLUTION',
  'RÉSOLUTION',
  'DEBIT',
  'DÉBIT',
  'FONCTIONNALITE',
  'FONCTIONNALITÉ',
  'SYSTEME',
  'SYSTÈME',
]);

const LABEL_TO_CRITERION_MAP = {
  'MARQUE': 'brand',
  'CATEGORIE': 'categorie',
  'CATEGORY': 'categorie',
  'PHASE': 'phase',
  'PUISSANCE': 'puissance',
  'CONNEXION': 'connectivity',
  'CONNECTIVITE': 'connectivity',
  'CONNECTIVITÉ': 'connectivity',
  'USAGE': 'usage',
  'SOLUTION': 'solution',
  'NORME': 'norme',
  'EQUIPEMENT': 'equipement',
  'ÉQUIPEMENT': 'equipement',
  'TECHNOLOGIE': 'technologie',
  'RESOLUTION': 'resolution',
  'RÉSOLUTION': 'resolution',
  'DEBIT': 'debit',
  'DÉBIT': 'debit',
  'FONCTIONNALITE': 'fonctionnalite',
  'FONCTIONNALITÉ': 'fonctionnalite',
  'SYSTEME': 'system',
  'SYSTÈME': 'system',
};

const CRITERION_DIRECT_FILTER_KEYS = [
  'brand', 'system', 'gasType', 'detectorType', 'usage', 'connectivity',
  'phase', 'puissance', 'norme', 'solution', 'equipement', 'typeGaz',
  'categorie', 'type', 'resolution', 'technologie', 'debit',
  'fonctionnalite', 'typeDetecteur', 'subcategory'
];
const VIRTUAL_CRITERION_PREFIX = '__criterion__:';
const VIRTUAL_CRITERION_VALUE_PREFIX = '__criterion_value__:';
const makeVirtualCriterionId = (criterionName) => `${VIRTUAL_CRITERION_PREFIX}${String(criterionName || '').trim()}`;
const makeVirtualCriterionValueId = (criterionName, criterionValue) => `${VIRTUAL_CRITERION_VALUE_PREFIX}${String(criterionName || '').trim()}::${String(criterionValue || '').trim()}`;
const isVirtualCriterionId = (value) => String(value || '').startsWith(VIRTUAL_CRITERION_PREFIX);
const isVirtualCriterionValueId = (value) => String(value || '').startsWith(VIRTUAL_CRITERION_VALUE_PREFIX);

const flattenFamilyTreeToOptions = (nodes) => {
  if (!Array.isArray(nodes)) return [];
  const out = [];
  const walk = (arr, trail) => {
    for (const n of arr) {
      if (!n || !n.name) continue;
      const nextTrail = [...trail, n.name];
      const label = nextTrail.join(' > ');
      out.push({ value: String(n.name).trim(), label });
      if (Array.isArray(n.children)) walk(n.children, nextTrail);
    }
  };
  walk(nodes, []);
  return out;
};

const flattenBranchOptions = (root) => {
  const out = []; const walk = (nodes, depth = 0) => {
    (nodes || []).forEach(n => {
      out.push({ value: n.id, label: `${'— '.repeat(depth)}${n?.type === 'value' ? '🍃 ' : ''}${n.name}` });
      if (n.children?.length) walk(n.children, depth + 1);
    });
  };
  if (root?.children?.length) walk(root.children, 1);
  return out;
};

// 🌳 CONVERT TO ANTD TREE DATA
//   - Accepte un array de branches dynamiques [{id,name,children,type}]
//   - OU un objet {fieldKey: {options:[...], isMultiple, label}} (categorySpecificFields)
//   - OU un mixed facets/options comme {fieldKey: values[]} + merged options
const buildAntTreeFromBranches = (branches, opts = {}) => {
  if (!branches) return [];
  // Cas 1: array branches (dynFamilyData.children)
  if (Array.isArray(branches)) {
    const walk = (nodes) => (nodes || []).map(n => {
      const isLeaf = !n.children || n.children.length === 0 || n.type === 'value';
      const icon = n.type === 'value' ? '🍃 ' : (n.icon ? `${n.icon} ` : '🔑 ');
      return {
        key: String(n.id ?? n.key ?? n.name),
        title: `${icon}${n.name ?? '(sans nom)'}`,
        children: isLeaf ? undefined : walk(n.children),
        _type: n.type,
        _rawId: n.id,
        _rawName: n.name,
      };
    });
    return walk(branches);
  }
  // Cas 2: objet {fieldKey: fieldConfig} → arbre par facette
  if (typeof branches === 'object') {
    const entries = Object.entries(branches);
    return entries.map(([fieldKey, cfg]) => {
      const fmeta = FACET_LABELS?.[fieldKey] || { label: (cfg && cfg.label) || fieldKey, icon: '🔹' };
      const options = Array.isArray(cfg && cfg.options) ? cfg.options : Array.isArray(cfg) ? cfg : [];
      const filteredOpts = options.filter(v => v != null && String(v).trim() !== '');
      return {
        key: `group:${fieldKey}`,
        title: (
          <span style={{ color: '#0f766e', fontWeight: 600 }}>
            {fmeta.icon || '🔹'} {fmeta.label}
          </span>
        ),
        selectable: false,
        children: filteredOpts.map((v, idx) => ({
          key: `val:${fieldKey}:${String(v)}:${idx}`,
          title: <>🍃 <span style={{ color: '#334155' }}>{String(v)}</span></>,
          _type: 'value',
          _fieldKey: fieldKey,
          _rawValue: v,
        })),
      };
    }).filter(n => n.children && n.children.length > 0);
  }
  return [];
};

// =====================================================================
// 🔧 UTILITAIRES PROFESSIONNELS — FACETTES & CRITÈRES
// =====================================================================
// Normalise une clé de facette (idempotent, accent insensible)
const normalizeFacetKey = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  return raw.trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
};

// 🔧 Parse en toute sécurité un objet facets (string JSON → object).
//    Indispensable: certaines réponses backend retournent facets comme string JSON
//    au lieu d'objet pré-désérialisé → buildUnifiedFamilyFacets lisait {} et rendait 0 critères.
const safeParseFacetsObj = (p) => {
  if (!p) return null;
  let f = p.facets;
  if (f == null) return null;
  if (typeof f === 'string') {
    try { f = JSON.parse(f); } catch { return null; }
  }
  if (typeof f === 'object' && !Array.isArray(f)) return f;
  return null;
};

// Ensemble des facettes DÉJÀ prises en charge HORS sections "spécifiques / critères"
// (Ces clés NE DOIVENT JAMAIS apparaître 2 fois — une fois en sélects officiels, une fois en arbres.)
const FACETTES_HORS_SPEC = new Set([
  // ——— FACETTES STANDARD (boutons/sélects OFFICIELS colonnes K..AI template Excel) ———
  'brand','marque','system','systeme','gastype','typedegas','gasdetectortype',
  'detectortype','typedetecteur','usage','utilisation','connectivity','connectivite',
  'norme','certification','certifie','solution','equipement','subcategory','souscategorie',
  'categorie','category','type','appareil','resolution','technologie','techno','debit',
  'fonctionnalite','fonction','typedetecteur','puissance','phase','phases','monophase',
  'triphase','tension','courant','frequence','protection','indice','ip','ip20','ip65',
  'garantie','materiau','couleur','montage','installation','rendement','capacitebatterie',
  'capacite','batterie','autonomie','typeonduleur','onduleur','connecteurs','sortie','entree',
  'origine','fabricant','fournisseur','compatibilite','interface','protocole','wifi','zigbee',
  'zwave','bluetooth','modbus','rs485','knx','bacnet','detection','alarme','sirene',
  'niveau','volume','sonore','visuel','memoire','stockage','donnees','logiciel',
  'firmware','version','modele','mode','etat','emplacement','zone','piece','bureau',
  'chambre','salon','cuisine','garage','jardin','batterietype','typebatterie','liion',
  'plomb','nimh','wattage','voltage','ampere','ah','kwh','kw','va','kva','hz',
  'classeenergetique','classe','a','b','c','d','e','f','g',
  'consommation','economie','efficacite','performance','debittempere','debitair',
  'flot','pression','temperature','humidite','ventilation','climatisation','chauffage',
  'radiateur','thermostat','regulation','sonde','capteur','actionneur','contacteur',
  'relais','disjoncteur','fusible','differenciel','interrupteur','prise','cable','fils',
  'gaine','tube','boitier','coffret','tableau','armoire','onduleurtype','chargesolaire',
  'solaire','photovoltaique','panneau','pv','module','cellule','injection','autoconsommation',
  'reseau','electrique','courantcontinu','courantalternatif','cc','ca','dc','ac',
  'secteur','batteriechargeur','chargeur','transformateur','convertisseur','dcdc','dac',
  'inverseur','redresseur','regulateur','stabilisateur','variateur','vitesse','moteur',
  'volet','roulage','store','porte','garagedoor','serrure','acces','badge','carte',
  'interphone','videophone','camera','dvr','nvr','enregistreur','video','hdmi','vga',
  'usb','ethernet','rj45','wifi24','wifi5','wifi6','4g','5g','lte','sim',
  'abonnement','cloud','abonnementcloud','serveur','stockagenuage','sd','microsd','hdd',
  'ssd','taille','format','dimensions','hauteur','largeur','profondeur','poids','masse',
  'gramme','kg','livre','unite','pack','carton','boite','piece','reference','sku','ean',
  'upc','gtin','codebarre','code','nom','name','title','titre','libelle',
  // ——— DESCRIPTIFS (ne jamais apparaitre en critères PERSONNALISÉS — description libre) ———
  'description','shortdescription','shortdesc','resume','sommaire','extrait',
  'descriptioncourte','descriptionlongue','detail','details','specifications',
  'specification','fichetechnique','fiche','datasheet','datasheets',
  // ↓↓↓ 15 VARIANTES orthographiques accents + tirets + espaces pour Caractéristique/Cara/Ctistiques ↓↓↓
  'caracteristique','caracteristiques','caracteristiquee','caracteristiquess',
  'caractristique','caractristiques','caractristiquee','caractristiquess',
  'caract','caracte','caracter','caracteri','caracteris','caracterist','caracteristi','caracteristiqu',
  'caractr','caractri','caractris','caractrist','caractristi','caractristiqu',
  'caracteristique','caracteristiques','carac','caracr','caracri','caracristique','caracristiques',
  'characteristics','characteristic','features','feature','spec','specs','attr','attribute','attributes',
  'informations','info','infos','presentation','contenu','contenudulivret',
  'livret','manuel','utilisation','guide','notice','avertissement','warning','note',
  'remarque','commentaire','commentaires','avis','exemple','casdusage','scenario',
  // ——— CHAMPS CORE + MÉTADONNÉES / CATÉGORIES + PAGINATION ———
  'family','famille','categorylevel1','categorylevel2','categorylevel3','categorylevel4','categorylevel5',
  'level1','level2','level3','level4','level5','niv1','niv2','niv3','niv4','niv5',
  'status','isvisible','isfeatured','tags','tag','id','ids','createdat','updatedat',
  'deletedat','search','recherche','category','instock','stock','disponible','rupture',
  'price','prix','maxprice','minprice','prixmax','prixmin','sortby','sortorder','trie',
  'page','limit','perpage','pagesize','currentpage','offset','include_facets','include',
  'expand','embed','token','auth','authorization','apikey','signature','nonce','timestamp',
  'createdby','updatedby','createdById','owner','user','userId','views','viewcount',
  'rating','avis','reviewcount','score','etoile','stars','favorite','wishlist','panier',
  'cart','order','commande','invoice','facture','delivery','livraison','shipping'
]);

/**
 * 🌟 MERGE LES 4 SOURCES DE FACETTES EN 1 MAP UNIFIÉE (garantie 100% données)
 *  Sources (ordre priorité):
 *    0) globalFacets     (/api/products?include_facets=true — la SOURCE LA PLUS FIABLE, toutes les facettes)
 *    1) categorySpecificFields (AdminFilters)
 *    2) sharedData.sharedTemplates (Sync BDD 12s)
 *    3) products[*].facets (produits chargés)
 */
const buildUnifiedFamilyFacets = (selectedCategory, categorySpecificFields, sharedTemplates, products, KNOWN_META, globalFacets) => {
  const unified = new Map();

  const upsert = (rawKey, opts = {}) => {
    const norm = normalizeFacetKey(rawKey);
    if (!norm) return;
    // ✅ DOUBLE GUARD #1: Blacklist HORS_SPEC (champs officiels / descriptifs / metadata)
    if (FACETTES_HORS_SPEC.has(norm)) return;
    // ✅ DOUBLE GUARD #2: rejette KEYS QUI RESSEMBLENT À DES VALEURS LIBRES (pas des noms de critères)
    //    ex: "Puissance: 3KVA", "Caractéristiques du produit", "Livraison gratuite sous 48h"
    if (norm.length > 50) return;                                                                 // phrase longue → c'est une VALEUR
    const rawTrim = String(rawKey || '').trim();
    if (rawTrim.length === 0) return;
    if (rawTrim.length > 80) return;                                                              // label trop long = valeur libre
    if (/^[\d\s\.\,\;\:\!\?\-]+$/.test(rawTrim)) return;                                          // seulement chiffres/ponctuation
    if (/[\n\r\t]/.test(rawTrim)) return;                                                          // contient passage ligne
    const colonCount = (rawTrim.match(/[\:\;]/g) || []).length;                                   // contient ":" ou ";"
    if (colonCount > 0) return;                                                                   // KEY NE DOIT PAS CONTENIR : ;
    const meta = KNOWN_META?.[norm] || KNOWN_META?.[rawKey];
    let entry = unified.get(norm);
    if (!entry) {
      entry = {
        key: norm,
        label: (meta?.label) || (opts?.label) || (String(rawKey).replace(/[_-]+/g,' ').replace(/\b\w/g, c => c.toUpperCase()).trim()),
        icon:  (meta?.icon)  || (opts?.icon)  || '🔹',
        isMultiple: !!(meta?.isMultiple ?? opts?.isMultiple ?? false),
        options: new Set(),
        hasProducts: false,
        hasAdminConfig: false,
        hasGlobal: false,
      };
      unified.set(norm, entry);
    } else {
      if (meta?.label && !opts.label) entry.label = meta.label;
      if (meta?.icon  && !opts.icon)  entry.icon  = meta.icon;
      if (meta?.isMultiple != null) entry.isMultiple = !!meta.isMultiple;
    }
    if (opts.fromAdmin) entry.hasAdminConfig = true;
    if (opts.fromProducts) entry.hasProducts = true;
    if (opts.fromGlobal) entry.hasGlobal = true;
    (opts.values || []).forEach(v => {
      if (v != null && String(v).trim() !== '') entry.options.add(String(v).trim());
    });
  };

  // ────────────────────────────────────────────────────────────────────
  // 0) globalFacets (OBJET GLOBAL API /include_facets — SOURCE #1 LA PLUS FIABLE)
  //    Format attendu: { 'Clé': ['Valeur1','Valeur2'], ... } ou { 'Clé': 'ValeurUnique' }
  // ────────────────────────────────────────────────────────────────────
  const glob = (globalFacets && typeof globalFacets === 'object') ? globalFacets : {};
  Object.entries(glob).forEach(([k, rawVals]) => {
    if (!k || typeof k !== 'string') return;
    const normK = normalizeFacetKey(k);
    if (!normK || FACETTES_HORS_SPEC.has(normK)) return;
    const values = Array.isArray(rawVals) ? rawVals : [rawVals];
    const hasAtLeastOne = values.some(v => v != null && String(v).trim() !== '');
    if (!hasAtLeastOne) return;
    upsert(k, { values, fromGlobal: true });
  });

  // ────────────────────────────────────────────────────────────────────
  // 1) categorySpecificFields (Admin config)
  // ────────────────────────────────────────────────────────────────────
  const specFields = (categorySpecificFields && typeof categorySpecificFields === 'object') ? categorySpecificFields : {};
  Object.entries(specFields).forEach(([k, cfg]) => {
    const normK = normalizeFacetKey(k);
    if (!normK || FACETTES_HORS_SPEC.has(normK)) return;
    const values = [];
    if (cfg?.options && Array.isArray(cfg.options)) values.push(...cfg.options);
    upsert(k, {
      label: cfg?.label || k,
      isMultiple: !!cfg?.isMultiple,
      values,
      fromAdmin: true,
    });
  });

  // ────────────────────────────────────────────────────────────────────
  // 2) sharedData.sharedTemplates
  // ────────────────────────────────────────────────────────────────────
  const shared = (sharedTemplates && typeof sharedTemplates === 'object') ? sharedTemplates : {};
  Object.entries(shared).forEach(([k, vals]) => {
    const normK = normalizeFacetKey(k);
    if (!normK || FACETTES_HORS_SPEC.has(normK)) return;
    if (!Array.isArray(vals)) return;
    upsert(k, { values: vals });
  });

  // ────────────────────────────────────────────────────────────────────
  // 3) products[*].facets (produits chargés pour la famille)
  // ────────────────────────────────────────────────────────────────────
  (products || []).forEach(p => {
    const facets = safeParseFacetsObj(p);
    if (!facets) return;
    Object.entries(facets).forEach(([k, rawV]) => {
      if (!k || typeof k !== 'string') return;
      const normK = normalizeFacetKey(k);
      if (!normK || FACETTES_HORS_SPEC.has(normK)) return;
      const values = Array.isArray(rawV) ? rawV : [rawV];
      const hasAtLeastOne = values.some(v => v != null && String(v).trim() !== '');
      upsert(k, {
        values,
        fromProducts: hasAtLeastOne,
      });
    });
  });

  // ────────────────────────────────────────────────────────────────────
  // 3b) 2ND PASS EXPLICITE: Force hasProducts=true MÊME si upsert guard
  //     (contre le bug "hasGlobal absorbe hasProducts")
  // ────────────────────────────────────────────────────────────────────
  (products || []).forEach(p => {
    const facets = safeParseFacetsObj(p);
    if (!facets) return;
    Object.keys(facets).forEach(k => {
      const normK = normalizeFacetKey(k);
      if (!normK || FACETTES_HORS_SPEC.has(normK)) return;
      const rawV = facets[k];
      const values = Array.isArray(rawV) ? rawV : [rawV];
      const hasAtLeastOne = values.some(v => v != null && String(v).trim() !== '');
      if (hasAtLeastOne) {
        const entry = unified.get(normK);
        if (entry) entry.hasProducts = true;
      }
    });
  });

  // ────────────────────────────────────────────────────────────────────
  // 3c) 3ÈME PASS ULTIME: GARANTIE ABSOLUE.
  //     Si une facette produit existe VRAIMENT (clé normalisée + valeur non-vide)
  //     mais n'est PAS dans unifiedMap → on l'AJOUTE DE FORCE.
  //     Contre: unifiedList=0 malgré produits=14+ (bug "globalFacets absorbe")
  // ────────────────────────────────────────────────────────────────────
  (products || []).forEach(p => {
    const facets = safeParseFacetsObj(p);
    if (!facets) return;
    Object.entries(facets).forEach(([k, rawV]) => {
      if (!k || typeof k !== 'string') return;
      const normK = normalizeFacetKey(k);
      if (!normK || FACETTES_HORS_SPEC.has(normK)) return;
      if (k === '__proto__' || k === 'constructor' || k === 'prototype') return;
      if (unified.has(normK)) return;
      if (normK.length > 80) return;
      const rawTrim = String(k).trim();
      if (rawTrim.length === 0 || rawTrim.length > 120) return;
      if (/^[\d\s\.\,\;\:\!\?\-]+$/.test(rawTrim)) return;
      if (/[\n\r\t]/.test(rawTrim)) return;
      const colonCount = (rawTrim.match(/[\:\;]/g) || []).length;
      if (colonCount > 0) return;
      const values = Array.isArray(rawV) ? rawV : [rawV];
      const hasAtLeastOne = values.some(v => v != null && String(v).trim() !== '');
      if (!hasAtLeastOne) return;
      const entry = {
        key: normK,
        label: String(k).replace(/[_-]+/g,' ').replace(/\b\w/g, c => c.toUpperCase()).trim(),
        icon: '🔹',
        isMultiple: false,
        options: new Set(values.filter(v => v != null && String(v).trim() !== '').map(v => String(v).trim())),
        hasProducts: true,
        hasAdminConfig: false,
        hasGlobal: false,
      };
      unified.set(normK, entry);
    });
  });

  const listBeforeFilter = Array.from(unified.values());
  const listFinal = listBeforeFilter
    .filter(e => (e.options.size > 0) || e.hasAdminConfig)
    .sort((a, b) => String(a.label).localeCompare(String(b.label), 'fr'));
  return listFinal;
};

/** Construit les nodes AntD Tree à partir de la liste unifiée */
const buildAntTreeFromUnified = (list, filters, onCheckFilterKey) => {
  return list.map(entry => {
    const key = entry.key;
    const optionsSorted = Array.from(entry.options).sort((a, b) => String(a).localeCompare(String(b), 'fr'));
    const currentFilter = filters?.[key];
    const checkedLeaves = [];
    optionsSorted.forEach((v, idx) => {
      let match = false;
      if (Array.isArray(currentFilter)) match = currentFilter.some(x => String(x) === String(v));
      else if (currentFilter != null && String(currentFilter) !== '') match = String(currentFilter) === String(v);
      if (match) checkedLeaves.push(`leaf:${key}:${idx}`);
    });
    return {
      key: `group:${key}`,
      title: (
        <span style={{ fontWeight: 600, color: entry.hasProducts ? '#0f766e' : '#64748b' }}>
          {entry.icon} {entry.label}
          {!entry.hasProducts && (
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400, marginLeft: 8 }}>(aucun produit)</span>
          )}
          {entry.isMultiple && <span style={{ fontSize: 11, color: '#0ea5e9', fontWeight: 500, marginLeft: 8 }}>· multi</span>}
          {entry.hasAdminConfig && !entry.hasProducts && (
            <Tag color="geekblue" style={{ marginLeft: 8, fontSize: 10 }}>ADMIN</Tag>
          )}
        </span>
      ),
      selectable: false,
      _facetKey: key,
      _isMultiple: entry.isMultiple,
      children: optionsSorted.length === 0
        ? [{ key: `empty:${key}`, title: <span style={{ color: '#94a3b8', fontSize: 12 }}>· (aucune valeur)</span>, selectable: false, checkable: false, disableCheckbox: true }]
        : optionsSorted.map((v, idx) => ({
            key: `leaf:${key}:${idx}`,
            title: <>🍃 <span style={{ color: '#334155' }}>{v}</span></>,
            _facetKey: key,
            _value: v,
            _idx: idx,
          })),
      _checkedLeaves: checkedLeaves, // sérialisé hors AntD pour onCheck plus bas
    };
  });
};

const mergeCategoryTreesRecursive = (backendNodes = [], legacyNodes = []) => {
  const outByName = new Map();
  backendNodes.forEach(bn => {
    const key = normalizeName(bn.name || '');
    outByName.set(key, {
      name: bn.name || (bn.slug ? String(bn.slug).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : `#${bn.id || 'x'}`),
      id: bn.id, slug: bn.slug, children: [], _isBackend: true, raw: bn
    });
  });
  legacyNodes.forEach(ln => {
    const key = normalizeName(ln.name || ''); if (!key) return;
    const existing = outByName.get(key);
    if (existing) {
      existing.children = mergeCategoryTreesRecursive(existing.children || [], ln.children || []);
      if (existing.id == null && ln.id != null) existing.id = ln.id;
    } else {
      outByName.set(key, {
        name: ln.name, id: ln.id, slug: ln.slug || null,
        children: Array.isArray(ln.children) ? mergeCategoryTreesRecursive([], ln.children) : [],
        _isBackend: false
      });
    }
  });
  return Array.from(outByName.values());
};

const { Title, Text, Paragraph } = AntTypography;
const { Panel } = Collapse;

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Legacy: kept for existing code refs but NOT used in the new filter
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedL2Id, setSelectedL2Id] = useState(null);
  const [selectedL3Id, setSelectedL3Id] = useState(null);
  const [categorySpecificFields, setCategorySpecificFields] = useState({});

  // =========================================================================
  // 🔑 🆕 FILTRE SIMPLIFIÉ — UNE SEULE LOGIQUE : CATÉGORIE → CRITÈRE → VALEUR
  //     Source UNIQUE : cascadeFamilyInfo.familyTree (GET /public/facets backend)
  //     Pas de cascadePath, pas de 2e système parallèle.
  // =========================================================================
  const [cascadeFamilyInfo, setCascadeFamilyInfo] = useState({ familles: [], familyTree: [] });
  // Les 3 variables UNIQUES du filtre (reset automatique quand on remonte le niveau)
  const [filterCategory, setFilterCategory] = useState(null); // = Excel "Famille", affiché comme CATÉGORIE
  const [filterCritere, setFilterCritere]   = useState(null); // = Critère N
  const [filterValeur,  setFilterValeur]    = useState(null); // = Valeur Critère N

  // ═══════════════════════════════════════════════════════════════
  // 🧩 V2 : FACETTES FILTRES MULTI-CRITÈRES (ET inter, OU intra)
  //    selectedFcPairs = [{nom: string, valeurs: string[]}]
  //    Exemple: [{nom:'Puissance',valeurs:['3 KVA','5 KVA']}, {nom:'Phase',valeurs:['Monophasé']}]
  //      = (Puissance = 3KVA OU 5KVA) ET (Phase = Mono)
  // ═══════════════════════════════════════════════════════════════
  const [criteriaAvailableV2, setCriteriaAvailableV2] = useState([]);
  const [selectedFcPairs, setSelectedFcPairs] = useState([]);
  const MAX_FC_PAIRS = 6;
  const addFcPairBloc = () => { if (selectedFcPairs.length < MAX_FC_PAIRS) setSelectedFcPairs(prev => [...prev, { nom: '', valeurs: [] }]); };
  const removeFcPairBloc = idx => setSelectedFcPairs(prev => prev.filter((_, i) => i !== idx));
  const updateFcPairNom = (idx, nom) => setSelectedFcPairs(prev => prev.map((p, i) => i === idx ? { nom: nom || '', valeurs: [] } : p));
  const updateFcPairValeurs = (idx, vals) => setSelectedFcPairs(prev => prev.map((p, i) => i === idx ? { ...p, valeurs: Array.isArray(vals) ? vals : [] } : p));
  const clearAllFcPairs = () => setSelectedFcPairs([]);
  // Helpers de navigation dans familyTree
  const normalize = (s) => String(s || '').trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');
  const selectedFamilyNode = (cascadeFamilyInfo.familyTree || []).find(
    f => normalize(f.famille) === normalize(selectedCategory?.name || filterCategory)
  ) || null;
  // Reset helpers
  const resetFilterAll = () => { setFilterCategory(null); setFilterCritere(null); setFilterValeur(null); clearAllFcPairs(); };
  const resetFilterCritere = () => { setFilterCritere(null); setFilterValeur(null); clearAllFcPairs(); };
  const resetFilterValeur = () => { setFilterValeur(null); };

  // ═══════════════════════════════════════════════════════════════
  // 🔑 CASCADE DYNAMIQUE : CATÉGORIE → NOM DE FILTRE → VALEUR
  //    Source UNIQUE : cascadeFamilyInfo.familyTree (GET /public/facets)
  //    calculé côté serveur sur TOUS les produits publiés et visibles.
  //    Aucun nom de filtre n'est prédéfini dans le code.
  // ═══════════════════════════════════════════════════════════════
  // Clés internes ("miroirs canoniques") pouvant exister comme ProductAttribute.criterion —
  // ce sont des champs système, jamais des noms de filtres Excel. Exact-match only.
  const CASCADE_INTERNAL_KEYS = new Set([
    'brand', 'system', 'gasType', 'typeGaz', 'detectorType', 'typeDetecteur', 'usage',
    'connectivity', 'location', 'phase', 'puissance', 'norme', 'solution', 'equipement',
    'categorie', 'type', 'resolution', 'technologie', 'debit', 'fonctionnalite',
    'functionality', 'subcategory', 'famille', 'category'
  ]);
  const dynamicCategoryOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    (cascadeFamilyInfo.familles || []).forEach(f => {
      const name = String(f || '').trim();
      if (!name) return;
      const k = normalize(name);
      if (seen.has(k)) return;
      seen.add(k);
      out.push({ value: name, label: name });
    });
    return out;
  }, [cascadeFamilyInfo]);

  // 🔑 FILTRES DISTINCTS & CUMULABLES : un groupe de valeurs par critère de la
  //    catégorie (Marque toujours + critères multi-valeurs, valeurs scopées par
  //    les autres filtres actifs — calculé côté backend familyTree).
  const dynamicCriteriaGroups = useMemo(() => {
    const criteres = (selectedFamilyNode && selectedFamilyNode.criteres) || [];
    const seen = new Set();
    const out = [];
    criteres.forEach(c => {
      const nom = String(c?.nom || '').trim();
      if (!nom) return;
      if (CASCADE_INTERNAL_KEYS.has(nom)) return;
      const k = normalize(nom);
      if (seen.has(k)) return;
      seen.add(k);
      const vals = Array.isArray(c?.valeurs) ? c.valeurs : [];
      const vSeen = new Set();
      const options = [];
      vals.forEach(v => {
        const val = String(v || '').trim();
        if (!val) return;
        const vk = normalize(val);
        if (vSeen.has(vk)) return;
        vSeen.add(vk);
        options.push({ value: val, label: val });
      });
      if (options.length === 0) return;
      out.push({ nom, label: nom, options });
    });
    return out;
  }, [selectedFamilyNode]);

  // Valeurs actuellement sélectionnées pour un critère donné (selectedFcPairs)
  const getPairValeurs = (nom) => {
    const p = (selectedFcPairs || []).find(pp => normalize(String(pp?.nom || '')) === normalize(nom));
    return p && Array.isArray(p.valeurs) ? p.valeurs : [];
  };

  // 🔑 CUMUL : sélection multi-valeurs par critère → selectedFcPairs
  //    (ET entre critères, OU entre valeurs d'un même critère)
  const handleCriterionValuesChange = (nom, vals) => {
    const clean = (Array.isArray(vals) ? vals : []).map(v => String(v || '').trim()).filter(Boolean);
    const nextPairs = (selectedFcPairs || []).filter(pp => normalize(String(pp?.nom || '')) !== normalize(nom));
    if (clean.length > 0) nextPairs.push({ nom, valeurs: clean });
    setSelectedFcPairs(nextPairs);
    // Compat : vider le legacy mono-critère (les groupes multi le remplacent)
    setFilterCritere(null);
    setFilterValeur(null);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    // Sync URL : fcPairs (JSON) — hydrate au prochain chargement
    const sp = new URLSearchParams(searchParams);
    for (let i = 1; i <= 50; i++) { sp.delete(`fc${i}_name`); sp.delete(`fc${i}_value`); }
    if (nextPairs.length > 0) {
      try { sp.set('fcPairs', JSON.stringify(nextPairs)); } catch (_) { sp.delete('fcPairs'); }
    } else {
      sp.delete('fcPairs');
    }
    sp.set('page', '1');
    setSearchParams(sp);
  };

  // Nettoie tous les critères dans l'objet filters (conserve recherche/tri)
  const clearCascadeFiltersObject = (prev) => {
    const next = { ...prev };
    Object.keys(next).forEach(k => {
      if (['search', 'sortBy', 'sortOrder', 'inStock', 'maxPrice', 'page', 'pageSize'].includes(k)) return;
      next[k] = '';
    });
    return next;
  };

  // NIVEAU 1 — Choisir/effacer la catégorie : réinitialise sous-cat + sous-sous-cat
  const handleDynamicCategoryChange = (v) => {
    const name = v ? String(v).trim() : '';
    setFilterCategory(name || null);
    setFilterCritere(null);
    setFilterValeur(null);
    clearAllFcPairs();
    setSelectedCategory(null);
    setSelectedL2Id(null);
    setSelectedL3Id(null);
    setCategorySpecificFields({});
    setDynFilters({});
    setDynFamilyId(null);
    setFilters(prev => clearCascadeFiltersObject(prev));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    const sp = new URLSearchParams(searchParams);
    ['category', 'subcategory', 'famille', 'fcPairs'].forEach(k => sp.delete(k));
    for (let i = 1; i <= 50; i++) { sp.delete(`fc${i}_name`); sp.delete(`fc${i}_value`); }
    if (name) sp.set('famille', name);
    sp.set('page', '1');
    setSearchParams(sp);
    if (!name) {
      // ✕ sur la catégorie : supprime le filtre + ses enfants et recharge tout de suite.
      //    _forceClean = ne pas réinjecter l'ancien état (fc1_*, famille, category)
      //    encore présent dans cette closure de render.
      lastTriggeredSigRef.current = '';
      loadDataLastSigRef.current = '';
      const obj = { page: 1, limit: pagination.itemsPerPage || 12, status: 'published', isVisible: true, _forceClean: true };
      if (filters?.search) obj.search = filters.search;
      loadData(obj);
    }
  };

  const [filters, setFilters] = useState({
    search: '',
    category: '',
    inStock: false,
    maxPrice: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    brand: '',
    system: '',
    subcategory: '',
    gasType: '',
    typeGaz: '',
    detectorType: '',
    usage: '',
    connectivity: '',
    norme: '',
    solution: '',
    equipement: '',
    categorie: '',
    type: '',
    resolution: '',
    technologie: '',
    debit: '',
    fonctionnalite: '',
    typeDetecteur: '',
    phase: '',
    puissance: ''
  });
  const [facets, setFacets] = useState({ });
  // Legacy helper (kept for refs in other parts of the codebase)
  const parseSpecificFields = (sf) => {
    if (!sf) return {};
    if (typeof sf === 'string') {
      try { const parsed = JSON.parse(sf); return (typeof parsed === 'object' && parsed !== null) ? parsed : {}; } catch(_) { return {}; }
    }
    return (typeof sf === 'object' && sf !== null) ? sf : {};
  };

  const [query, setQuery] = useState('');
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [collapseOpen, setCollapseOpen] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12
  });
  const resultsTopRef = useRef(null);
  const shouldScrollAfterPaginationRef = useRef(false);
  const [sharedFiltersVersion, setSharedFiltersVersion] = useState(0);
  const [dynFamilyData, setDynFamilyData] = useState(null);
  const [dynFamilyLoading, setDynFamilyLoading] = useState(false);
  const [dynFilters, setDynFilters] = useState({});
  const [dynFamilyId, setDynFamilyId] = useState(null);
  const [backendCategoryVersion, setBackendCategoryVersion] = useState(0);
  // Legacy: cascadePath kept for references but NOT populated by the new UI
  const [cascadePath, setCascadePath] = useState([]);

  const scrollToResultsTop = () => {
    const node = resultsTopRef.current;
    if (!node) return;

    const activeElement = document.activeElement;
    if (activeElement && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }

    const headerOffset = 150;
    const targetTop = Math.max(
      0,
      node.getBoundingClientRect().top + window.scrollY - headerOffset
    );

    node.scrollIntoView({ block: 'start', behavior: 'auto' });
    window.scrollTo(0, targetTop);
    document.documentElement.scrollTop = targetTop;
    document.body.scrollTop = targetTop;
  };

  const scheduleScrollToResultsTop = () => {
    scrollToResultsTop();
    requestAnimationFrame(scrollToResultsTop);
    setTimeout(scrollToResultsTop, 80);
    setTimeout(() => {
      scrollToResultsTop();
      shouldScrollAfterPaginationRef.current = false;
    }, 180);
  };

  useLayoutEffect(() => {
    if (!shouldScrollAfterPaginationRef.current || isLoading) {
      return;
    }

    scheduleScrollToResultsTop();
  }, [isLoading, products, pagination.currentPage, pagination.itemsPerPage]);

  const readSharedFilters = () => {
    try {
      if (window.__domoelecFiltersStore && typeof window.__domoelecFiltersStore === 'object') {
        return window.__domoelecFiltersStore;
      }
      let raw = null;
      try { raw = sessionStorage.getItem('domoelec:facetTemplates'); } catch { raw = null; }
      if (raw) return JSON.parse(raw) || null;
      try { raw = localStorage.getItem('domoelec:facetTemplates'); } catch { raw = null; }
      if (raw) return JSON.parse(raw) || null;
      return null;
    } catch { return null; }
  };

  // ⚠️ TOUTES LES REFS GLOBALES (anti-double-loadData) DÉCLARÉES ICI AVANT hardResetFiltersAndState
  const initialResetDoneRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const loadDataLastSigRef = useRef('');
  const loadDataRequestCounterRef = useRef(0);  // 🔑 ANTI-RACE: seul la réponse du dernier appel est appliquée
  const hasEverLoadedRef = useRef(false);       // true après la 1re requête produits → overlay léger ensuite (plus de spinner pleine page)
  const lastTriggeredSigRef = useRef('');   // 🔑 DÉDUPLICATEUR FILTRE UNIQUE (évite double-appel React StrictMode)
  const skipNextFilterEffectRef = useRef(false);
  const richestProductsMetaRef = useRef({ facetKeysSize: -1, idsHash: '' });
  const filtersStateRef = useRef(filters);
  useEffect(() => { filtersStateRef.current = filters; }, [filters]);
  const facetsStateRef = useRef(facets);
  useEffect(() => { facetsStateRef.current = facets; }, [facets]);

// 🧭 DÉMARRAGE ÉTAPE 1/3 — Lit les searchParams de l'URL et les conserve.
//   SANS exécuter hardReset pour l'instant (hardReset détruit setSearchParams).
//   -> Charge les filtres React depuis URL (category, subcategory, search, page, fc1_*, etc.)
const applySearchParamsToReactState = () => {
  const sp = searchParams;
  const hasAnyQuery = Array.from(sp.keys()).length > 0;
  if (!hasAnyQuery) {
    // Cas normal: URL vide → reset complet des états (filtres, query, pagination).
    const emptyFilters = {
      search: '',
      category: '',
      sortBy: 'createdAt',
      sortOrder: 'desc',
      inStock: false,
      maxPrice: '',
      pageSize: pagination.itemsPerPage || 12,
    };
    if (facets && typeof facets === 'object') {
      Object.keys(facets).forEach(k => { emptyFilters[k] = ''; });
    }
    setQuery('');
    setCascadePath([]);
    setDynFilters({});
    setDynFamilyId(null);
    setDynFamilyData(null);
    setSelectedCategory(null);
    setSelectedL2Id(null);
    setSelectedL3Id(null);
    setCategorySpecificFields({});
    resetFilterAll();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    loadDataLastSigRef.current = '';
    skipNextFilterEffectRef.current = false;
    richestProductsMetaRef.current = { facetKeysSize: -1, idsHash: '' };
    setFilters(emptyFilters);
    return;
  }
  // URL a des paramètres: on préserve la query string.
  //   -> Construit un objet filtre React initial à partir de searchParams.
  const merged = {
    ...filters,
    search: (sp.get('search') || '').trim(),
    category: (sp.get('category') || '').trim(),
    subcategory: (sp.get('subcategory') || '').trim(),
    sortBy: sp.get('sortBy') || 'createdAt',
    sortOrder: sp.get('sortOrder') || 'desc',
    pageSize: pagination.itemsPerPage || 12,
    inStock: (sp.get('inStock') === 'true'),
    maxPrice: sp.get('maxPrice') || '',
    brand: sp.get('brand') || '',
    system: sp.get('system') || '',
    gasType: sp.get('gasType') || '',
    typeGaz: sp.get('typeGaz') || '',
    detectorType: sp.get('detectorType') || '',
    usage: sp.get('usage') || '',
    connectivity: sp.get('connectivity') || '',
    norme: sp.get('norme') || '',
    solution: sp.get('solution') || '',
    equipement: sp.get('equipement') || '',
    categorie: sp.get('categorie') || '',
    type: sp.get('type') || '',
    resolution: sp.get('resolution') || '',
    technologie: sp.get('technologie') || '',
    debit: sp.get('debit') || '',
    fonctionnalite: sp.get('fonctionnalite') || '',
    typeDetecteur: sp.get('typeDetecteur') || '',
    phase: sp.get('phase') || '',
    puissance: sp.get('puissance') || '',
  };
  setQuery(merged.search);
  setFilters(merged);
  setCascadePath([]);
  setDynFilters({});
  setDynFamilyId(null);
  setDynFamilyData(null);
  setSelectedCategory(null);
  setSelectedL2Id(null);
  setSelectedL3Id(null);
  resetFilterAll();
  // Hydrater la cascade dynamique depuis l'URL (?famille=..&fc1_name=..&fc1_value=..)
  const urlFamille = (sp.get('famille') || '').trim();
  const urlFc1Name = (sp.get('fc1_name') || '').trim();
  const urlFc1Value = (sp.get('fc1_value') || '').trim();
  if (urlFamille) setFilterCategory(urlFamille);
  if (urlFc1Name) setFilterCritere(urlFc1Name);
  if (urlFc1Value) setFilterValeur(urlFc1Value);
  // 🔑 Hydrater les filtres cumulables depuis l'URL : ?fcPairs=[{nom,valeurs:[..]}]
  //    + compat : fc1_name/fc1_value → une paire visible dans les groupes.
  try {
    const hydrated = [];
    const rawPairs = sp.get('fcPairs');
    if (rawPairs) {
      const arr = JSON.parse(rawPairs);
      if (Array.isArray(arr)) {
        arr.forEach(p => {
          const nom = String(p?.nom || p?.critere || p?.name || '').trim();
          const vals = (Array.isArray(p?.valeurs) ? p.valeurs : [p?.valeur ?? p?.value])
            .map(v => String(v ?? '').trim()).filter(Boolean);
          if (nom) hydrated.push({ nom, valeurs: vals });
        });
      }
    }
    if (hydrated.length === 0 && urlFc1Name && urlFc1Value) {
      hydrated.push({ nom: urlFc1Name, valeurs: [urlFc1Value] });
    }
    if (hydrated.length > 0) setSelectedFcPairs(hydrated);
  } catch (_) {}
  try {
    const pageParam = parseInt(sp.get('page'), 10);
    if (!Number.isNaN(pageParam) && pageParam >= 1) {
      setPagination(prev => ({ ...prev, currentPage: pageParam }));
    } else {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }
  } catch {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }
  loadDataLastSigRef.current = '';
  skipNextFilterEffectRef.current = false;
  richestProductsMetaRef.current = { facetKeysSize: -1, idsHash: '' };
};

useEffect(() => {
  applySearchParamsToReactState();
  // eslint-disable-next-line
}, []);

// 🧭 DÉMARRAGE ÉTAPE 2/3 — UN SEUL LOAD GARANTI (categories + facets prêts)
useEffect(() => {
  if (!(categories && categories.length > 0)) return;
  if (facets == null) return;
  if (initialResetDoneRef.current) return;
  initialResetDoneRef.current = true;
  initialLoadDoneRef.current = false;
  loadDataLastSigRef.current = '';
  // 🔑 NOUVEAU: construit les overrideFilters à partir des searchParams RESTANTS
  //    (category=controle-acces-pointage, subcategory, page, search, etc.)
  //    → loadData ne fera plus setSearchParams(VIDE) puisqu'on ne l'apelle plus !
  const sp = searchParams;
  const overrideParams = { status: 'published', isVisible: true };
  for (const [k, v] of sp.entries()) {
    if (k === 'page') continue;
    if (k === 'limit') continue;
    if (k === 'pageSize') continue;
    if (v != null && v !== '') overrideParams[k] = v;
  }
  try {
    const pageVal = parseInt(sp.get('page'), 10);
    overrideParams.page = Number.isFinite(pageVal) && pageVal >= 1 ? pageVal : 1;
    overrideParams.limit = pagination.itemsPerPage || 12;
  } catch {
    overrideParams.page = 1;
    overrideParams.limit = pagination.itemsPerPage || 12;
  }
  try {
    loadData(overrideParams).finally(() => {
      initialLoadDoneRef.current = true;
    });
  } catch {
    initialLoadDoneRef.current = true;
  }
  // eslint-disable-next-line
}, [categories.length, facets]);

// (ANCIEN useEffect désactivé searchParams.toString(). Pas besoin ici.)

// ✅ Fonction manuelle pour lancer la recherche
const handleSearch = () => {
  const searchValue = query.trim();
  setFilters(prev => ({ ...prev, search: searchValue }));
  setPagination(prev => ({ ...prev, currentPage: 1 }));

  // Synchroniser l'URL avec les paramètres de recherche
  const next = new URLSearchParams(searchParams);
  if (searchValue) {
    next.set('search', searchValue);
  } else {
    next.delete('search');
  }
  next.set('page', '1');
  // Ne pas forcer exact=1 automatiquement, laisser l'utilisateur choisir
  setSearchParams(next);

  // ══════════════════════════════════════════════════════════
  // 🔑 SPEC: Appel filtre Excel DIRECT immédiat (valeurs capturées ci-dessous)
  //   Plus de useEffect fragmenté (plus de bug React StrictMode sig identique
  //   + async state qui retarde fc1_value. handleSearch utilise les états
  //   React filterCritere / filterValeur / selectedLeafCategory qui ont été
  //   commités au moment du click Rechercher (bouton cliqué après les selects).
  // ══════════════════════════════════════════════════════════
  try {
    const familleDirect = (selectedCategory?.name?.trim() || filterCategory?.trim() || '');
    const critereDirect = (filterCritere && String(filterCritere).trim()) || '';
    const valeurDirect = (filterValeur && String(filterValeur).trim()) || '';
    if (familleDirect !== '' || filters?.category || critereDirect) {
      loadDataLastSigRef.current = '';
      const obj = {
        page: 1,
        limit: pagination.itemsPerPage || 12,
        status: 'published',
        isVisible: true,
      };
      const backendCat = getEffectiveCategoryRef()
        || ((filters?.category && String(filters.category).trim() !== '')
          ? String(filters.category).trim()
          : '');
      if (backendCat) obj.category = backendCat;
      else if (familleDirect) obj.famille = familleDirect;
      // 🔑 Nom/valeur de filtre 100% dynamiques — le backend canonise fc1_name lui-même
      if (critereDirect) obj.fc1_name = critereDirect;
      if (critereDirect && valeurDirect) obj.fc1_value = valeurDirect;
      if (searchValue) obj.search = searchValue;
      loadData(obj);
    }
  } catch (_) {}
};

// ═══════════════════════════════════════════════════════════════
// 🔑 🎯 CANON_FACET: CRITÈRES aliasés EXCEL → FACETTE DÉDIÉE
//    Si colonne Excel "Marque" est interprétée comme Critère → REDIRIGER vers filters.brand
//    (apparaît comme MARQUE dans L2/L3 sidebar, PAS dans dropdown Critère générique)
// ═══════════════════════════════════════════════════════════════
const CANON_FACET_FROM_CRITERE = [
  { aliases: ['Marque','MARQUE','marque','Brand','BRAND','brand','Fabricant','FABRICANT','fabricant'], canon: 'brand', label: '🏷️ Marque' },
  { aliases: ['Systeme','SYSTEME','systeme','Système','SYSTÈME','système','System','SYSTEM','system','Famille_systeme','famille_systeme'], canon: 'system', label: '⚙️ Système' },
  { aliases: ['Type_de_gaz','TYPE_DE_GAZ','Type de gaz','TYPE DE GAZ','type_de_gaz','Gaz_type','GAZ_TYPE','gaz_type','Gas_type','GAS_TYPE','gas_type','Gaz','GAZ','gaz'], canon: 'gasType', label: '💨 Type de gaz' },
  { aliases: ['Type_de_detecteurs','TYPE_DE_DÉTECTEURS','Type de détecteurs','TYPE DE DÉTECTEURS','type_de_detecteurs','Detecteur_type','DÉTECTEUR_TYPE','detecteur_type','Type_detecteur','TYPE_DÉTECTEUR','type_detecteur','Detector_type','DETECTOR_TYPE','detector_type'], canon: 'detectorType', label: '🔍 Type de détecteur' },
  { aliases: ['Usage','USAGE','usage','Utilisation','UTILISATION','utilisation','Application','APPLICATION','application'], canon: 'usage', label: '🎯 Usage' },
  { aliases: ['Connectivite','CONNECTIVITÉ','connectivite','Connectivité','connectivité','Connexion','CONNEXION','connexion','Connectivity','CONNECTIVITY','connectivity'], canon: 'connectivity', label: '📶 Connectivité' },
  { aliases: ['Fonctionnalites','FONCTIONNALITÉS','fonctionnalites','Fonctionnalités','fonctionnalités','Fonctionnalites_specifiques'], canon: 'functionality', label: '✨ Fonctionnalités' },
  { aliases: ['Localisation','LOCALISATION','localisation','Lieu','LIEU','lieu','Zone','ZONE','zone'], canon: 'location', label: '📍 Localisation' },
  { aliases: ['Phase','PHASE','phase','Alimentation','ALIMENTATION','alimentation'], canon: 'phase', label: '⚡ Phase / Alimentation' },
  { aliases: ['Puissance','PUISSANCE','puissance','Power','POWER','power','Watt','WATT','watt'], canon: 'puissance', label: '🔌 Puissance' },
  { aliases: ['Norme','NORME','norme','Norme_standard','NORME_STANDARD','norme_standard','Standard','STANDARD','standard'], canon: 'norme', label: '📏 Norme' },
  { aliases: ['Solution','SOLUTION','solution','Gamme','GAMME','gamme'], canon: 'solution', label: '🎁 Solution / Gamme' },
  { aliases: ['Equipement','ÉQUIPEMENT','equipement','Equipment','EQUIPEMENT','equipment','Type_equipement',"TYPE_D'ÉQUIPEMENT",'type_equipement'], canon: 'equipement', label: '🛠️ Équipement' },
  { aliases: ['Type_gaz','TYPE_GAZ','type_gaz','TypeGaz','TYPEGAZ','typegaz'], canon: 'typeGaz', label: '🧪 Type gaz' },
  { aliases: ['Categorie_spec','CATÉGORIE_SPÉC','categorie_spec','Catégorie_spécifique','Spec_categorie','SPÉC_CATÉGORIE','spec_categorie','Categorie','CATÉGORIE','categorie'], canon: 'categorie', label: '📂 Catégorie spécifique' },
  { aliases: ['Type_spec','TYPE_SPÉC','type_spec','Type_spécifique','Spec_type','SPÉC_TYPE','spec_type','Type','TYPE','type'], canon: 'type', label: '🏷️ Type spécifique' },
  { aliases: ['Resolution','Résolution','RÉSOLUTION','resolution'], canon: 'resolution', label: '🖥️ Résolution' },
  { aliases: ['Technologie','TECHNOLOGIE','technologie','Techno','TECHNO','techno','Technology','TECHNOLOGY','technology'], canon: 'technologie', label: '🔬 Technologie' },
  { aliases: ['Debit','DÉBIT','debit','Flow','FLOW','flow'], canon: 'debit', label: '🌊 Débit' },
  { aliases: ['Fonctionnalite','FONCTIONNALITÉ','fonctionnalite','Fonc','FONC','fonc'], canon: 'fonctionnalite', label: '⚙️ Fonctionnalité' },
  { aliases: ['Type_detecteur_spec','TYPE_DÉTECTEUR_SPÉC','type_detecteur_spec','Type_détecteur_spécifique','Detecteur_spec','DÉTECTEUR_SPÉC','detecteur_spec','Détecteur_spécifique'], canon: 'typeDetecteur', label: '🔎 Type détecteur spéc.' },
  { aliases: ['Sous_categorie_spec','SOUS-CATÉGORIE_SPÉC','sous_categorie_spec','Sous-catégorie_spécifique','Sous_categorie','SOUS-CATÉGORIE','sous_categorie','Sous-catégorie'], canon: 'subcategory', label: '📁 Sous-catégorie spéc.' },
];
const _canonCritereToFacet = (nomCritere) => {
  const n = String(nomCritere || '').trim();
  if (!n) return null;
  const nUp = n.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
  for (const rule of CANON_FACET_FROM_CRITERE) {
    const hit = rule.aliases.some(a => {
      const aUp = String(a || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
      return aUp === nUp;
    });
    if (hit) return rule.canon;
  }
  return null;
};
const _isCanonCritereName = (nom) => !!_canonCritereToFacet(nom);

// ═══════════════════════════════════════════════════════════════
// 🔑 HELPER: construit la liste de params fc (fcPairs + fcN_name/fcN_value flat)
//    depuis filterCritere/filterValeur (legacy 1 critère) + selectedFcPairs (V2 multi)
// ═══════════════════════════════════════════════════════════════
const buildFcParams = (customFcPairsOverride) => {
  const params = {};
  const finalPairs = [];
  const canonFacetsDirect = {};

  if (Array.isArray(customFcPairsOverride)) {
    for (const p of customFcPairsOverride) finalPairs.push(p);
  } else {
    // Legacy 1 critère filterCritere / filterValeur
    const legNom = String(filterCritere || '').trim();
    const legVal = String(filterValeur || '').trim();
    if (legNom) {
      const canonLeg = _canonCritereToFacet(legNom);
      if (canonLeg && legVal) {
        // 🔑 Si critère = Marque/Système/... → redirect vers FACETTE DIRECTE (pas fc)
        canonFacetsDirect[canonLeg] = legVal;
      } else {
        finalPairs.push({ nom: legNom, valeurs: legVal ? [legVal] : [] });
      }
    }
    // V2 multi blocs (ET inter, OU intra)
    if (Array.isArray(selectedFcPairs)) {
      for (const p of selectedFcPairs) {
        const n = String(p?.nom || '').trim();
        const vs = Array.isArray(p?.valeurs) ? p.valeurs.map(v => String(v || '').trim()).filter(Boolean) : [];
        if (!n) continue;
        const canon = _canonCritereToFacet(n);
        if (canon && vs.length === 1) {
          // Valeur unique → param facette direct (?brand=EATON)
          canonFacetsDirect[canon] = vs[0];
        } else {
          const existing = finalPairs.find(fp => normalize(fp.nom) === normalize(n));
          if (existing) {
            const merged = Array.from(new Set([...existing.valeurs, ...vs]));
            existing.valeurs = merged;
          } else {
            finalPairs.push({ nom: n, valeurs: vs });
          }
        }
      }
    }
  }

  const cleanPairs = finalPairs.filter(p => p && typeof p === 'object' && String(p.nom || '').trim());
  if (cleanPairs.length > 0) {
    try { params.fcPairs = JSON.stringify(cleanPairs); } catch (_) {}
    cleanPairs.forEach((p, idx) => {
      const n = idx + 1;
      params[`fc${n}_name`] = String(p.nom).trim();
      if (Array.isArray(p.valeurs) && p.valeurs.length === 1) {
        params[`fc${n}_value`] = String(p.valeurs[0]).trim();
      }
    });
  }
  // Injecte les facettes canoniques directes (Marque → brand, …) en dehors des fc
  Object.assign(params, canonFacetsDirect);
  return { params, cleanPairs, canonFacetsDirect };
};

// ═══════════════════════════════════════════════════════════════
// 🔑 HELPER UNIQUE: Applique filtre CRITERE + VALEUR DIRECTEMENT
//    (plus de useEffect cascade SPEC#7, plus de dépendance state async)
//    Appelé PARTOUT où CRITERE / VALEUR change ou click Rechercher.
//    newCritere = nouveau nom critère sélectionné
//    newValeur  = nouvelle valeur sélectionnée ('' ou null pour vider)
//    resetValeur = true → reset value UI (onChange Critère → reset value)
// ═══════════════════════════════════════════════════════════════
const applyExcelFilterDirect = (newCritere, newValeur, { resetValeur = false, alsoClearMultiPairs = false } = {}) => {
  try {
    if (alsoClearMultiPairs) clearAllFcPairs();
    // 1) Mettre à jour les états UI Select (valeurs prochaines):
    if (resetValeur) setFilterValeur(null);
    if (newCritere !== undefined) setFilterCritere(newCritere || null);
    if (newValeur !== undefined) setFilterValeur(newValeur || null);

    // 2) Chargement DIRECT avec les VALEURS QUE L'ON VIENT D'APPLIQUER
    //    (pas de setState async capture, on utilise explicitement newCritere/newValeur)
    const familleNow = (selectedCategory?.name?.trim() || filterCategory?.trim() || '');
    if (familleNow === '' && !(filters?.category)) return; // besoin famille OU catégorie L1
    const critereNow = String(newCritere || '').trim();
    const valeurNow = String(resetValeur ? '' : (newValeur || '')).trim();
    const limit = pagination.itemsPerPage || 12;
    loadDataLastSigRef.current = ''; // bypass signature guard dedup

    // 🔑 Si le critère est un ALIAS CANONIQUE (Marque → brand) → ON UTILISE LA FACETTE DIRECTE
    //    Et on GARDE obj.category = L1 SQL (detection-incendie / controle-acces-pointage / ...).
    const canonFromCrit = critereNow ? _canonCritereToFacet(critereNow) : null;

    const obj = {
      page: 1,
      limit,
      status: 'published',
      isVisible: true,
    };
    // Toujours setter la catégorie SQL L1 SI elle existe (priorité !)
    const backendCat = (filters?.category && String(filters.category).trim() !== '')
      ? String(filters.category).trim()
      : (selectedCategory?.slug || (selectedCategory?.id != null ? String(selectedCategory.id) : ''));
    if (backendCat) obj.category = backendCat;

    if (canonFromCrit) {
      // 🎯 Marque/Système/... : SET directement facette canonique → buildFacetWhere backend l'attrape
      if (valeurNow) obj[canonFromCrit] = valeurNow;
      // Famille seulement si pas de catégorie backend connue
      if (!obj.category && familleNow) obj.famille = familleNow;
    } else {
      // Legacy fc1 / famille Excel
      if (!obj.category && familleNow) obj.famille = familleNow;
      if (critereNow) obj.fc1_name = critereNow;
      if (critereNow && valeurNow) obj.fc1_value = valeurNow;
    }

    // Injecte multi-crit V2 depuis state courant (sauf si on force le legacy via CRITERE reset)
    if (!critereNow && Array.isArray(selectedFcPairs) && selectedFcPairs.some(p => String(p?.nom || '').trim())) {
      const { params } = buildFcParams([{ nom: '', valeurs: [] }]);
    }
    if (filters?.search) obj.search = filters.search;
    loadData(obj);
  } catch (_) {}
};

// 🔍 Suggestions pour autocomplete (debounce 250ms, ne déclenche PAS de filtre)
const [suggestions, setSuggestions] = useState([]);
useEffect(() => {
  const q = query.trim();
  if (q.length < 1) { setSuggestions([]); return; }
  const timer = setTimeout(async () => {
    try {
      const res = await publicProductService.getSuggestions(q, 8);
      const items = (res.suggestions || []).map((p) => ({
        value: p.name,
        slug: p.slug,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.name}
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>{p.reference}</span>
          </div>
        ),
      }));
      setSuggestions(items);
    } catch (_) {
      setSuggestions([]);
    }
  }, 250);
  return () => clearTimeout(timer);
}, [query]);

// 📡 Charger les données quand filters ou pagination changent
// ⚠️ GARDE: SKIP PENDANT LE DÉMARRAGE (initialLoadDoneRef = false) pour éviter la compétition
//    de triggers setState qui font loadData() avec filtres pas encore réinitialisés → 0 produit.
const categorySpecificFiltersKey = useMemo(() => {
  if (!categorySpecificFields || Object.keys(categorySpecificFields).length === 0) {
    return '';
  }
  const specificKeys = Object.keys(categorySpecificFields);
  const specificValues = specificKeys
    .map(key => `${key}:${filters[key] || ''}`)
    .sort()
    .join('|');
  return specificValues;
}, [
  categorySpecificFields,
  JSON.stringify(
    Object.keys(categorySpecificFields || {}).reduce((acc, key) => {
      acc[key] = filters[key] || '';
      return acc;
    }, {})
  )
]);

const catFiltersKey = useMemo(() => {
  return Object.entries(filters)
    .filter(([k]) => k.startsWith('cat_'))
    .map(([k, v]) => `${k}=${v || ''}`)
    .sort()
    .join('|');
}, [filters]);

const allDynamicFacetsKey = useMemo(() => {
  const allFacetKeys = new Set([
    ...Object.keys(facets || {}),
    ...Object.keys(categorySpecificFields || {})
  ]);
  return Array.from(allFacetKeys)
    .sort()
    .map(k => `${k}=${filters[k] == null ? '' : String(filters[k])}`)
    .join('|');
}, [facets, categorySpecificFields, JSON.stringify(
  Array.from(new Set([...Object.keys(facets || {}), ...Object.keys(categorySpecificFields || {})]))
    .sort()
    .reduce((acc, k) => { acc[k] = filters[k]; return acc; }, {})
)]);

useEffect(() => {
  // 🔑 GARDE ANTI-COMPÉTITION AU DÉMARRAGE
  if (!initialLoadDoneRef.current) return;

  // 🛡️ GARDE handleCascadeSelectLevel a déjà appelé loadData(directLoad) DIRECTEMENT → SKIP CE TRIGGER
  if (skipNextFilterEffectRef.current) {
    skipNextFilterEffectRef.current = false;
    return;
  }

  if (filters && Object.keys(filters).length > 0) {
    const timer = setTimeout(() => {
      loadData();
    }, 250);
    return () => clearTimeout(timer);
  }
}, [
  filters.search,
  filters.category,
  filters.inStock,
  filters.maxPrice,
  filters.sortBy,
  filters.sortOrder,
  pagination.currentPage,
  categorySpecificFiltersKey,
  catFiltersKey,
  allDynamicFacetsKey
]);


  // 🔑 FIX #1: RÉINITIALISATION AUTO CRITÈRE + VALEUR QUAND CHANGEMENT CATÉGORIE (N1/N2/N3)
  //      Spec: "Quand je change de CATÉGORIE : reset critère / valeur / tempWizardValues / appliedFacetFilters / fcN"
  useEffect(() => {
    resetFilterCritere();  // reset filterCritere + filterValeur
    // eslint-disable-next-line
  }, [selectedCategory?.id, selectedL2Id, selectedL3Id]);

  // 🔧 FIX SYNC FC1 DANS L'OBJET FILTERS (défense en profondeur)
  //    Quand filterCritere / filterValeur changent → les copier DANS filters.fc1_name / filters.fc1_value
  //    Pour que le useEffect filters (ligne 1022) qui lance loadData() SANS arguments
  //    récupère bien les fc params depuis activeFilters = {...filters}.
  useEffect(() => {
    const fam = String(filterCategory || '').trim();
    const f1n = String(filterCritere || '').trim();
    const f1v = String(filterValeur || '').trim();
    setFilters(prev => {
      const nextFamille = prev.famille || '';
      const nextFc1Name = prev.fc1_name || '';
      const nextFc1Value = prev.fc1_value || '';
      if (nextFamille === fam && nextFc1Name === f1n && nextFc1Value === f1v) return prev;  // 0 changement → pas de re-render
      const next = { ...prev, famille: fam, fc1_name: f1n, fc1_value: f1v };
      // Clean up legacy multi-paires : elles ne doivent jamais survivre à la cascade
      next.fcPairs = '';
      Object.keys(next).forEach(k => {
        if (/^fc\d+_(name|value)$/i.test(k) && !['fc1_name', 'fc1_value'].includes(k)) delete next[k];
      });
      return next;
    });
    // ⚠️ PAS de skipNextFilterEffectRef ici : famille/fc1_* ne sont PAS des deps du
    //    useEffect filters → il ne se redéclenche pas → le flag resterait "armé" et
    //    avalerait le PROCHAIN changement légitime (pagination, tri, recherche...).
  }, [filterCategory, filterCritere, filterValeur]);

  // Charger les catégories une seule fois
  useEffect(() => {
    (async () => {
      try {
        const categoriesResponse = await publicCategoryService.getCategories();
        setCategories(deepRecover(categoriesResponse.categories || []));
        const facetsResponse = await publicProductService.getFacets();
        const loadedFacets = deepRecover(facetsResponse.facets || {});
        setFacets(loadedFacets);
        // 🔑 NOUVEAU: charger familles + familyTree isolé par famille
        if (Array.isArray(facetsResponse.familles) || Array.isArray(facetsResponse.familyTree)) {
          setCascadeFamilyInfo({
            familles: deepRecover(Array.isArray(facetsResponse.familles) ? facetsResponse.familles : []),
            familyTree: deepRecover(Array.isArray(facetsResponse.familyTree) ? facetsResponse.familyTree : [])
          });
        }
        if (Array.isArray(facetsResponse.criteriaAvailable)) {
          setCriteriaAvailableV2(deepRecover(facetsResponse.criteriaAvailable));
        }
        // Initialiser TOUTES les nouvelles clés facets dans filters (évite undefined)
        setFilters(prev => {
          const updated = { ...prev };
          Object.keys(loadedFacets).forEach(k => {
            if (updated[k] === undefined) updated[k] = '';
          });
          return updated;
        });
      } catch (err) {
        /* console.error('Erreur lors du chargement des catégories:', err); */
      }
    })();
  }, []);

  // 🔑 Recharger les facettes SCOPÉES à la catégorie + critères actifs
  //    → filtres cumulables : les valeurs de chaque critère restent visibles et
  //    se réduisent selon les AUTRES critères sélectionnés (calculé côté backend).
  const lastFacetScopeRef = useRef('|');
  const fcPairsSig = useMemo(() => (selectedFcPairs || [])
    .map(p => `${String(p?.nom || '')}:${(Array.isArray(p?.valeurs) ? p.valeurs.join(',') : '')}`)
    .join('||'), [selectedFcPairs]);
  useEffect(() => {
    const scopeKey = `${filters?.category || ''}|${filters?.subcategory || ''}|${fcPairsSig}|${filterCritere || ''}|${filterValeur || ''}`;
    if (lastFacetScopeRef.current === scopeKey) return;
    lastFacetScopeRef.current = scopeKey;
    let cancelled = false;
    (async () => {
      const params = {};
      if (filters?.category) params.category = filters.category;
      if (filters?.subcategory) params.subcategory = filters.subcategory;
      // Critères actifs (fcPairs + legacy fc1_*) → valeurs scopées par critère
      try {
        const { params: fcParamsExtra } = buildFcParams();
        Object.assign(params, fcParamsExtra || {});
      } catch (_) {}
      const facetsResponse = await publicProductService.getFacets(params);
      if (cancelled || !facetsResponse) return;
      const loadedFacets = deepRecover(facetsResponse.facets || {});
      setFacets(loadedFacets);
      if (Array.isArray(facetsResponse.familles) || Array.isArray(facetsResponse.familyTree)) {
        setCascadeFamilyInfo({
          familles: deepRecover(Array.isArray(facetsResponse.familles) ? facetsResponse.familles : []),
          familyTree: deepRecover(Array.isArray(facetsResponse.familyTree) ? facetsResponse.familyTree : [])
        });
      }
      if (Array.isArray(facetsResponse.criteriaAvailable)) {
        setCriteriaAvailableV2(deepRecover(facetsResponse.criteriaAvailable));
      }
    })();
    return () => { cancelled = true; };
  }, [filters?.category, filters?.subcategory, fcPairsSig, filterCritere, filterValeur]);

  // 🔥 Suppression doublon useEffect facets [filters.category] (L979-L1001) — cause ERR_ABORTED
  //    + setFacets boucle infinie: setFacets→setFilters→useEffect[filters]→loadData→re-render→...
  //    Pas de polling infini. Chargement initial + agrégation loadData produits.
  useEffect(() => {
    if (!selectedCategory?.id) { setDynFamilyData(null); setDynFilters({}); setDynFamilyId(null); return; }
    const id = Number(selectedCategory.id);
    if (id === dynFamilyId) return;
    let cancelled = false;
    (async () => {
      try {
        setDynFamilyLoading(true); setDynFamilyId(id);
        const { family } = await publicCategoryService.getFamily(id);
        if (cancelled) return;
        setDynFamilyData(family || null);
      } catch (e) { setDynFamilyData(null); } finally { if (!cancelled) setDynFamilyLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedCategory?.id]);

  const rootCategories = useMemo(() => (categories || []).filter(c => !c.parentCategoryId && !c.parentCategory), [categories]);

  const sharedData = useMemo(() => {
    const p = readSharedFilters();
    const sharedTemplates = (p && typeof p.templates === 'object') ? p.templates : {};
    const sharedFamilyTree = Array.isArray(p && p.familyTree) ? p.familyTree : [];
    const sharedCategoryFlatOpts = flattenFamilyTreeToOptions(sharedFamilyTree);
    return { sharedTemplates, sharedFamilyTree, sharedCategoryFlatOpts };
  }, [sharedFiltersVersion, rootCategories]);

  // 🔁 BUILD & MERGE backend categories (priorité 1) + storage legacy (fallback)
  const BACKEND_TREE = useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return [];
    const byId = new Map();
    categories.forEach(c => { byId.set(String(c.id), { ...c, children: [] }); });
    const roots = [];
    byId.forEach(node => {
      const pid = node.parentCategoryId || node.parentCategory?.id;
      if (pid != null && byId.has(String(pid))) byId.get(String(pid)).children.push(node);
      else roots.push(node);
    });
    const sortFn = (a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
    const walk = nodes => { nodes.sort(sortFn); nodes.forEach(n => { if (Array.isArray(n.children) && n.children.length) walk(n.children); }); };
    walk(roots);
    return roots;
  }, [categories, backendCategoryVersion]);

  const CATEGORY_TREE_MERGED = useMemo(() => mergeCategoryTreesRecursive(BACKEND_TREE, sharedData.sharedFamilyTree || []), [BACKEND_TREE, sharedData.sharedFamilyTree]);

  // Helper: enfants directs d'une catégorie (DÉCLARÉ AVANT useMemo resolvedPathBackendNodes — évite ReferenceError "before initialization")
  const getChildren = useCallback((cat) => {
    if (!cat) return [];
    const cid = typeof cat === 'object' ? cat.id : cat;
    return (categories || []).filter(c =>
      c.parentCategoryId === cid ||
      (c.parentCategory && c.parentCategory.id === cid)
    );
  }, [categories]);

  const isRealCategoryNode = useCallback((node, parentNode = null) => {
    if (!node || !String(node.name || '').trim()) return false;
    if (node.isVirtual) return true;
    const type = String(node.type || node.raw?.type || '').trim().toLowerCase();
    if (type === 'criterion' || type === 'value') return false;

    const nodeName = normalizeName(node.name || '');
    const parentName = normalizeName(parentNode?.name || '');

    // Evite les faux niveaux issus des critères ou des doublons du parent.
    if (parentName && nodeName === parentName) return false;
    if (RESERVED_CATEGORY_LABELS.has(nodeName)) return false;
    if (RESERVED_CATEGORY_LABELS.has(parentName)) return false;

    return true;
  }, []);

  // Helper: TOUTES les feuilles d'une catégorie (récursif 4+ niveaux)
  const getAllLeaves = useCallback((cat, visited = null) => {
    if (!cat) return [];
    const v = visited || new Set();
    const cid = typeof cat === 'object' ? cat.id : cat;
    if (v.has(cid)) return [];
    v.add(cid);
    const c = typeof cat === 'object' ? cat : (categories || []).find(x => String(x.id) === String(cid));
    if (!c) return [];
    const kids = getChildren(c);
    if (kids.length === 0) return [c];
    const out = [];
    kids.forEach(k => { out.push(...getAllLeaves(k, v)); });
    return out;
  }, [categories, getChildren]);

  const getMergedNodeAtPath = (path) => {
    if (!Array.isArray(path) || path.length === 0) return null;
    let nodes = CATEGORY_TREE_MERGED; let current = null;
    for (const pn of path) {
      current = nodes.find(n => normalizeName(n.name) === normalizeName(pn));
      if (!current) return null;
      nodes = current.children || [];
    }
    return current;
  };
  const getMergedChildrenOfPath = (path) => {
    if (!Array.isArray(path) || path.length === 0) return CATEGORY_TREE_MERGED;
    const node = getMergedNodeAtPath(path);
    return node?.children || [];
  };

  // 🔧 GENERIC: Label d'un niveau de cascade (N niveaux supportés)
  const getCascadeLevelLabel = (levelIndex) => {
    switch (levelIndex) {
      case 0: return 'CATÉGORIE';
      case 1: return 'SOUS-CATÉGORIE';
      case 2: return 'SOUS-SOUS-CATÉGORIE';
      case 3: return 'SOUS-SOUS-SOUS-CATÉGORIE';
      default: return `CATÉGORIE (NIVEAU ${levelIndex + 1})`;
    }
  };

  // ⭐ Cascade helpers (identique Admin ProductForm)
  const resolvedPathBackendNodes = useMemo(() => {
    const out = []; let parentBackendId = null;
    for (const nodeName of cascadePath) {
      let bc = null;
      const wanted = normalizeName(nodeName);
      if (parentBackendId != null) {
        const direct = getChildren(parentBackendId).find(c => normalizeName(c.name) === wanted);
        if (direct) bc = direct; else {
          const desc = []; const q = [...getChildren(parentBackendId)];
          while (q.length) { const n = q.shift(); if (normalizeName(n.name) === wanted && !bc) bc = n; getChildren(n.id).forEach(c => q.push(c)); }
        }
      }
      if (!bc) {
        const roots = categories.filter(c => !(c.parentCategoryId || c.parentCategory?.id));
        bc = roots.find(c => normalizeName(c.name) === wanted) || categories.find(c => normalizeName(c.name) === wanted) || null;
      }
      out.push(bc);
      if (bc) parentBackendId = bc.id; else parentBackendId = null;
    }
    return out;
  }, [cascadePath, categories]);

  // 🔧 GENERIC: Children of any path (N niveaux) — FALLBACK ROBUSTE 3 NIVEAUX
  //    Règle LOGIQUE UTILISATEUR:
  //      selectedParent (path) → children
  //      ≡ items.filter(item => item.parentId === selectedParentId)
  //
  //    Pour TOUT niveau (pas seulement 1 ou 2):
  //      T1) CATEGORY_TREE_MERGED (legacy + backend fusionnés)
  //      T2) BACKEND_TREE direct (parentCategoryId MySQL, pas legacy écrasé)
  //      T3) Recalcul manuel backend via getChildren(parentId) — ÉQUIVALENT À
  //          resolvedPathBackendNodes mais DÉCLARÉ EN LIGNE SANS référence
  //          directe à la variable useMemo (évite TDZ ReferenceError).
  //
  //    JAMAIS de if (category==="X") { show X1,X2... } — purement DATA-DRIVEN.
  const getChildrenOfPathWithFallback = useCallback((path) => {
    if (!Array.isArray(path) || path.length === 0) return [];
    const norm = (s) => normalizeName(s || '');
    let children = [];

    // T1: CATEGORY_TREE_MERGED walk by path
    const mergedKids = getMergedChildrenOfPath(path);
    if (Array.isArray(mergedKids) && mergedKids.length > 0) children = mergedKids;

    // T2: BACKEND_TREE walk by name path (identique structure, pas écrasé par legacy)
    if (children.length === 0) {
      let bk = BACKEND_TREE;
      for (let i = 0; i < path.length; i++) {
        const hit = (bk || []).find(n => norm(n.name) === norm(path[i]));
        if (!hit) { break; }
        if (i === path.length - 1) {
          if (Array.isArray(hit.children) && hit.children.length > 0) children = hit.children;
        } else {
          bk = hit.children || [];
        }
      }
    }

    // T3: Recalcul backend EN LIGNE via getChildren(parentId)
    //    (Équivaut exactement au useMemo resolvedPathBackendNodes mais sans
    //     référence directe à sa variable → 0 risque TDZ)
    if (children.length === 0) {
      let parentBackendId = null;
      for (let i = 0; i < path.length; i++) {
        let bc = null;
        const wanted = norm(path[i]);
        if (parentBackendId != null) {
          bc = getChildren(parentBackendId).find(c => norm(c.name) === wanted);
        }
        if (!bc) {
          const roots = (categories || []).filter(c => !(c.parentCategoryId || (c.parentCategory && c.parentCategory.id)));
          bc = roots.find(c => norm(c.name) === wanted)
            || (categories || []).find(c => norm(c.name) === wanted)
            || null;
        }
        if (i === path.length - 1 && bc) {
          const direct = (bc.id != null) ? getChildren(bc.id) : [];
          if (Array.isArray(direct) && direct.length > 0) children = direct;
          break;
        }
        if (bc) parentBackendId = bc.id; else break;
      }
    }

    return Array.isArray(children) ? children : [];
  }, [BACKEND_TREE, categories]);

  const cascadeFinalId = (() => {
    const last = [...resolvedPathBackendNodes].reverse().find(Boolean);
    return last ? last.id : null;
  })();
  const cascadeSelectedRoot = (resolvedPathBackendNodes[0]?.id != null) ? resolvedPathBackendNodes[0] : null;
  // Synchro cascadePath -> selectedCategory (pour les anciens hooks qui dépendent: spécific fields, champs dynamiques)
  // → SEULEMENT selectedCategory (plus legacy L2/L3 qui causaient AND subcategory=∅)
  useEffect(() => {
    if (cascadeSelectedRoot && (!selectedCategory || String(selectedCategory.id) !== String(cascadeSelectedRoot.id))) {
      setSelectedCategory(cascadeSelectedRoot);
      setCategorySpecificFields(parseSpecificFields(cascadeSelectedRoot.specificFields) || {});
    }
    // Nettoyage legacy L2/L3 toujours null (on n'utilise plus ce système)
    if (selectedL2Id) setSelectedL2Id(null);
    if (selectedL3Id) setSelectedL3Id(null);
  }, [cascadeSelectedRoot?.id, cascadePath.length, resolvedPathBackendNodes.join('|')]);

  const handleCascadeSelectLevel = (levelIndex, nodeName) => {
    const newPath = cascadePath.slice(0, levelIndex);
    if (nodeName) newPath.push(nodeName);
    setCascadePath(newPath);
    setDynFilters({});
    setDynFamilyId(null);
    // 🔥🔥🔥 SYSTÉMATIQUE (tout changement de catégorie / niveau de cascade):
    //    RAZ des filtres facettes TEMP (non appliqués) + APPLIQUÉS (filtrage frontend) + selectedCritereKey
    //    => sans ça, appliedFacetFilters d'une CATÉGORIE PRÉCÉDENTE reste coincé
    //       → affiche 0 produit au lieu de tous les produits de la nouvelle catégorie
    setSelectedCritereKey(null);
    setTempWizardValues({});
    setAppliedFacetFilters({});

    // Calcul FRESH depuis CATEGORY_TREE_MERGED (pas stale state)
    let catNode = null;
    let curLevel = CATEGORY_TREE_MERGED;
    for (let i = 0; i < newPath.length; i++) {
      const nn = newPath[i];
      const hit = Array.isArray(curLevel) ? curLevel.find(c => normalizeName(c.name) === normalizeName(nn)) : null;
      if (!hit) { break; }
      catNode = hit;
      curLevel = hit.children || [];
    }
    let backendCat = null;
    if (catNode && (catNode._isBackend || catNode._backendId != null)) {
      backendCat = { id: catNode._backendId || catNode.id };
    }
    if ((!backendCat || backendCat.id == null) && catNode && catNode.name) {
      // fallback byName
      backendCat = categories.find(c => normalizeName(c.name) === normalizeName(catNode.name)) || null;
    }

    // 🔥🔥🔥 MERGED FEUILLES → MULTI-CATÉGORIES BACKEND (backend supporte category=id1,id2,id3)
    //   But: un noeud "CONTRÔLE D'ACCÈS" merged peut avoir des enfants LEGACY (ouss) + BACKEND (ZKTecco)
    //        → Backend ne connaît pas legacy children, donc on charge CHAQUE feuille merged (backend resolve)
    const mergedAllLeafIds = new Set();
    const resolveCatNodeToId = (node) => {
      if (!node) return null;
      const numericId = node._backendId ?? node.id;
      if (numericId != null && Number.isFinite(Number(numericId))) return Number(numericId);
      // Fallback par nom dans categories backend
      if (node?.name) {
        const want = normalizeName(node.name);
        const bc = categories.find(c => normalizeName(c.name) === want);
        if (bc?.id != null) return Number(bc.id);
      }
      return null;
    };
    const walkMergedLeaves = (nodes) => {
      if (!Array.isArray(nodes)) return;
      nodes.forEach(node => {
        const hasChildren = Array.isArray(node.children) && node.children.length > 0;
        if (!hasChildren) {
          // Feuille: résoudre ID backend
          const id = resolveCatNodeToId(node);
          if (id != null) mergedAllLeafIds.add(id);
        } else {
          walkMergedLeaves(node.children);
        }
      });
    };
    // Cas spécial: si catNode existe, on considère catNode ET ses descendants (pour cas où catNode est une feuille elle-même)
    if (catNode) {
      const selfId = resolveCatNodeToId(catNode);
      if (selfId != null) mergedAllLeafIds.add(selfId);
      if (Array.isArray(catNode.children)) walkMergedLeaves(catNode.children);
    }
    // Fallback si aucun merged (catNode introuvable): utiliser backendCat classique
    if (mergedAllLeafIds.size === 0 && backendCat?.id != null) {
      mergedAllLeafIds.add(Number(backendCat.id));
    }
    // Augmenter limit pour avoir plus de produits si multi-catégories
    const adaptiveLimit = mergedAllLeafIds.size > 1
      ? Math.max(pagination.itemsPerPage || 12, Math.min(48, (pagination.itemsPerPage || 12) * mergedAllLeafIds.size))
      : (pagination.itemsPerPage || 12);

    const nf = { ...filters };
    Object.keys(nf).forEach(k => {
      if (k === 'category' || k === 'subcategory' || k.startsWith('cat_') || k.startsWith('dyn_')) {
        delete nf[k];
      }
    });
    if (mergedAllLeafIds.size > 0) {
      // ✅ Support backend natif: category=35,190,... → rawCategoryList.split(',') → multi descendants
      nf.category = Array.from(mergedAllLeafIds).map(String).join(',');
    } else if (catNode && newPath.length > 0) {
      // Fallback historique (si merged pas résolu)
      if (backendCat?.id != null) {
        nf.category = String(backendCat.id);
      } else if (catNode?.id != null && Number.isFinite(Number(catNode.id))) {
        nf.category = String(catNode.id);
      } else if (catNode?.slug) {
        nf.category = String(catNode.slug);
      }
    }

    // ⚠️ BYPASS setState async React : CONSTRUIRE DIRECT params loadData avec nf
    // (setFilters(nf) est ASYNCHRONE → useEffect filters déclenche loadData() PLUS TARD
    //  et parfois lit l'ancien state avec filters.category='' → 0 produit)
    const directParams = { ...nf };
    // Reset des champs réservés dans directParams (comme loadData() fait)
    Object.keys(directParams).forEach(k => {
      if (k.startsWith('cat_') || k.startsWith('dyn_')) delete directParams[k];
      if (k === 'subcategory') delete directParams[k];
    });
    const directLoad = {
      ...directParams,
      page: 1,
      limit: adaptiveLimit,
      status: 'published',
      isVisible: true,
    };
    // Nettoyer directLoad comme loadData()
    Object.keys(directLoad).forEach(key => {
      const value = directLoad[key];
      if (value === '' || value === undefined || value === null) { delete directLoad[key]; return; }
      if (key === 'inStock' && value === false) { delete directLoad[key]; return; }
      if (key === 'search' && (!value || String(value).trim() === '')) { delete directLoad[key]; return; }
    });
    // Bypass dedup: force call (même si signature identique)
    loadDataLastSigRef.current = '';
    // 🛡️ DÉCLENCHER LA GARDE 1: setFilters(nf) ci-dessous déclenche useEffect → SKIP (on a déjà loadData(directLoad))
    skipNextFilterEffectRef.current = true;
    // 🛡️ RESET GARDE 2: nouvelle catégorie → accepter le PROCHAIN setProducts même si moins riche
    richestProductsMetaRef.current = { facetKeysSize: -1, idsHash: '' };

    setFilters(nf);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    const q = new URLSearchParams(searchParams);
    Object.keys(nf).forEach(k => {
      const v = nf[k];
      if (v === undefined || v === null || v === '' || v === false) q.delete(k); else q.set(k, String(v));
    });
    q.set('page', '1');
    setSearchParams(q);

    // ✅ APPEL DIRECT LOAD DATA (pas attendre setState async)
    try { loadData(directLoad); } catch {}
  };

  useEffect(() => {
    const bump = () => setSharedFiltersVersion(v => v + 1);
    const iv = setInterval(bump, 8000);
    window.addEventListener('storage', bump);
    window.addEventListener('domoelec:filters-updated', bump);
    window.addEventListener('focus', bump);
    let bc = null;
    try { bc = new BroadcastChannel('domoelec-filters-channel'); } catch { bc = null; }
    if (bc) {
      const onBcMsg = e => {
        if (e?.data?.type === 'filters-updated') {
          try { if (e.data.payload) window.__domoelecFiltersStore = e.data.payload; } catch { /* ignore */ }
          bump();
        }
      };
      try { bc.onmessage = onBcMsg; } catch { /* ignore */ }
    }
    return () => {
      clearInterval(iv);
      window.removeEventListener('storage', bump);
      window.removeEventListener('domoelec:filters-updated', bump);
      window.removeEventListener('focus', bump);
      try { if (bc) bc.close && bc.close(); } catch { /* ignore */ }
    };
  }, []);

  // Helper: enfants directs d'une catégorie — DÉCLARÉ PLUS HAUT (avant useMemo resolvedPathBackendNodes) — voir CATEGORY_TREE_MERGED bloc.
  // Helper: TOUTES les feuilles — déclaré plus haut aussi.

  // Sous-catégories de la catégorie sélectionnée (niveau 2) — uniquement les vraies catégories enfants
  const subCategories = useMemo(() => {
    if (!selectedCategory) return [];
    
    // 1) Récupérer les sous-catégories réelles du backend
    const realSubCats = getChildren(selectedCategory).filter(c => isRealCategoryNode(c, selectedCategory));
    
    // 2) Injecter les critères Excel de cette famille (Nom Filtre 1, 2, etc.) comme nœuds virtuels
    //    C'est la "logique vidéo surveillance" : transformer les critères techniques en niveaux de dropdown
    const virtualNodes = (selectedFamilyNode?.criteres || []).map(crit => ({
      id: `virtual_crit_${crit.nom}`,
      name: String(crit.nom).toUpperCase(),
      slug: `virtual_crit_${crit.nom}`,
      type: 'criterion',
      isVirtual: true,
      _virtualCriterion: crit
    }));

    // Fusionner en évitant les doublons (si une catégorie réelle porte le même nom qu'un critère, on garde la réelle)
    const all = [...realSubCats];
    virtualNodes.forEach(vn => {
      const alreadyExists = all.some(c => normalizeName(c.name) === normalizeName(vn.name));
      if (!alreadyExists) {
        all.push(vn);
      }
    });

    return all;
  }, [getChildren, isRealCategoryNode, selectedCategory, selectedFamilyNode]);

  // LABEL FACETTES (icône + libellé FR + multi ou non)
  const FACET_LABELS = {
    brand: { label: 'Marque', icon: '🏷️', isMultiple: false },
    puissance: { label: 'Puissance', icon: '⚡', isMultiple: false },
    phase: { label: 'Phase', icon: '🔌', isMultiple: false },
    tension: { label: 'Tension', icon: '🔋', isMultiple: false },
    frequence: { label: 'Fréquence', icon: '📊', isMultiple: false },
    technologie: { label: 'Technologie', icon: '🧬', isMultiple: false },
    norme: { label: 'Norme / Certification', icon: '✅', isMultiple: true },
    protection: { label: 'Indice de protection', icon: '🛡️', isMultiple: false },
    garantie: { label: 'Garantie', icon: '📆', isMultiple: false },
    materiau: { label: 'Matériau', icon: '🧱', isMultiple: false },
    couleur: { label: 'Couleur', icon: '🎨', isMultiple: false },
    montage: { label: 'Montage', icon: '🔧', isMultiple: false },
    rendement: { label: 'Rendement', icon: '📈', isMultiple: false },
    capaciteBatterie: { label: 'Capacité batterie', icon: '🔋', isMultiple: false },
    typeOnduleur: { label: "Type d'onduleur", icon: '🔁', isMultiple: false },
    connecteurs: { label: 'Connecteurs / Sorties', icon: '🔗', isMultiple: true },
    origine: { label: 'Origine', icon: '🌍', isMultiple: false },
    system: { label: 'Système', icon: '🖥️', isMultiple: false },
    typeGaz: { label: 'Type de gaz', icon: '💨', isMultiple: false },
    detectorType: { label: 'Type de détecteur', icon: '📡', isMultiple: false },
    usage: { label: "Usage / Domaine d'application", icon: '🏢', isMultiple: false },
    connectivity: { label: 'Connectivité', icon: '📶', isMultiple: false },
    resolution: { label: 'Résolution', icon: '📷', isMultiple: false },
    debit: { label: 'Débit', icon: '💧', isMultiple: false },
    fonctionnalite: { label: 'Fonctionnalité', icon: '⚙️', isMultiple: true },
    categorie: { label: 'Catégorie produit', icon: '📦', isMultiple: false },
    type: { label: 'Type', icon: '🧩', isMultiple: false },
    solution: { label: 'Solution', icon: '💡', isMultiple: false },
    equipement: { label: 'Équipement', icon: '🛠️', isMultiple: false },
  };

  function toOptions(values = []) {
    return Array.from(new Set(values.filter(Boolean)))
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((v) => ({ value: v, label: v }));
  }

  // 🔽 NIVEAU 1 (CATÉGORIE) — RACINES (identique structure à l2Options/l3Options)
  const l1Options = useMemo(() => {
    return (rootCategories || [])
      .filter(c => isRealCategoryNode(c))
      .filter(c => c && c.name)
      .map(c => ({ value: String(c.slug || c.id), label: String(c.name), id: c.id, slug: c.slug }));
  }, [isRealCategoryNode, rootCategories]);

  // 🔽 NIVEAU 2 (SOUS-CATÉGORIE) — UNIQUEMENT SI catégorie niveau 1 est sélectionnée
  const l2Options = useMemo(() => {
    if (!selectedCategory) return [];
    const opts = subCategories
      .filter(c => c && c.name)
      .map(c => ({ value: String(c.slug || c.id), label: String(c.name), id: c.id, slug: c.slug }));
    return opts;
  }, [subCategories, selectedCategory]);

  const l2SelectedObj = useMemo(() => {
    if (!selectedL2Id) return null;
    return subCategories.find(c => String(c.slug || c.id) === String(selectedL2Id)) || null;
  }, [subCategories, selectedL2Id]);

  // 🔽 NIVEAU 3 (SOUS-SOUS-CATÉGORIE) — UNIQUEMENT SI niveau 2 sélectionné
  const l3Options = useMemo(() => {
    if (!l2SelectedObj) return [];
    
    // Si le niveau 2 est un critère virtuel (ex: PHASE), le niveau 3 affiche ses valeurs (ex: Monophasé, Triphasé)
    if (l2SelectedObj.isVirtual && l2SelectedObj._virtualCriterion) {
      const values = l2SelectedObj._virtualCriterion.valeurs || [];
      return toOptions(values).map(opt => ({
        ...opt,
        id: `virtual_val_${opt.value}`,
        slug: opt.value,
        isVirtual: true
      }));
    }

    const kids = getChildren(l2SelectedObj).filter(c => isRealCategoryNode(c, l2SelectedObj));
    return kids
      .filter(c => c && c.name)
      .map(c => ({ value: String(c.slug || c.id), label: String(c.name), id: c.id, slug: c.slug }));
  }, [getChildren, isRealCategoryNode, l2SelectedObj]);

  const l3SelectedObj = useMemo(() => {
    if (!selectedL3Id || !l2SelectedObj) return null;
    const kids = getChildren(l2SelectedObj);
    return kids.find(c => String(c.slug || c.id) === String(selectedL3Id)) || null;
  }, [selectedL3Id, l2SelectedObj, categories]);

  const getEffectiveCategoryRef = useCallback(() => {
    const isVirtual = l2SelectedObj?.isVirtual || VIRTUAL_FACET_PARENT_LABELS.has(normalizeName(l2SelectedObj?.name || ''));
    const deepestNode = l3SelectedObj || l2SelectedObj || selectedCategory || null;
    if (deepestNode && !isVirtual) {
      return String(deepestNode.slug || deepestNode.id || '').trim();
    }
    return selectedCategory ? String(selectedCategory.slug || selectedCategory.id || '').trim() : '';
  }, [l2SelectedObj, l3SelectedObj, selectedCategory]);

  useEffect(() => {
    const targetRef = String(filters?.subcategory || '').trim();
    if (!targetRef || !selectedCategory) return;

    const directL2 = (subCategories || []).find(c => String(c.slug || c.id) === targetRef);
    if (directL2) {
      if (String(selectedL2Id || '') !== String(directL2.slug || directL2.id)) setSelectedL2Id(String(directL2.slug || directL2.id));
      if (selectedL3Id) setSelectedL3Id(null);
      return;
    }

    for (const l2 of (subCategories || [])) {
      const l3 = getChildren(l2).find(c => String(c.slug || c.id) === targetRef);
      if (l3) {
        const nextL2 = String(l2.slug || l2.id);
        const nextL3 = String(l3.slug || l3.id);
        if (String(selectedL2Id || '') !== nextL2) setSelectedL2Id(nextL2);
        if (String(selectedL3Id || '') !== nextL3) setSelectedL3Id(nextL3);
        return;
      }
    }
  }, [filters?.subcategory, selectedCategory, subCategories, getChildren, selectedL2Id, selectedL3Id]);

  // 🔑 HELPER: Catégorie LA PLUS PROFONDE SÉLECTIONNÉE (feuille N3 || N2 || N1)
  //    Doit être APRÈS l2SelectedObj/l3SelectedObj pour éviter la TDZ ReferenceError.
  const selectedLeafCategory = l3SelectedObj || l2SelectedObj || selectedCategory;

  useEffect(() => {
    const ensureMetaTag = (attrName, attrValue) => {
      let meta = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attrName, attrValue);
        document.head.appendChild(meta);
      }
      return meta;
    };

    let canonicalUrl = `${SITE_URL}/produits`;
    // Titre/description identiques à backend/seo.js ROUTES['/produits']
    // (cohérence entre le HTML initial et le DOM après chargement React).
    let title = 'Matériel courant faible et sécurité au Maroc | DOMOELEC';
    let description = 'Découvrez les produits DOMOELEC : détection incendie, vidéosurveillance, contrôle d’accès, onduleurs et domotique. Consultez les fiches et demandez un devis.';
    let robots = 'index,follow';

    const categorySlug = String(searchParams.get('category') || '').trim();
    const familleName = String(searchParams.get('famille') || '').trim();
    const searchValue = String(searchParams.get('search') || '').trim();
    // URLs canoniques : ?category=slug / ?famille=nom (mêmes règles que
    // backend/seo.js). page=1 = la page elle-même, pas un filtre ; tout
    // autre paramètre (recherche, tri, fcPairs, page>1…) = combinaison
    // filtrée → noindex, mais la canonical garde l'identité de la page.
    const hasExtraFilters = [...searchParams.keys()].some(key => {
      if (key === 'category' || key === 'famille') return false;
      if (key === 'page' && String(searchParams.get('page') || '').trim() === '1') return false;
      return true;
    });

    const breadcrumb = [
      { name: 'Accueil', item: `${SITE_URL}/` },
      { name: 'Produits', item: `${SITE_URL}/produits` }
    ];
    let leafName = '';

    // Catégorie résolue uniquement si elle correspond au paramètre URL
    // courant — sinon une réponse tardive écraserait la catégorie
    // sélectionnée ensuite (course async categories/selectedCategory).
    const resolvedCategory =
      selectedCategory?.slug && (!categorySlug || selectedCategory.slug === categorySlug)
        ? selectedCategory
        : null;
    const effectiveCatSlug = resolvedCategory?.slug || categorySlug;

    if (effectiveCatSlug) {
      canonicalUrl = `${SITE_URL}/produits?category=${encodeURIComponent(effectiveCatSlug)}`;
      leafName = resolvedCategory?.name || categorySlug;
      if (resolvedCategory) {
        title = resolvedCategory.metaTitle || `${resolvedCategory.name} | Produits DOMOELEC`;
        description = (
          resolvedCategory.metaDescription ||
          resolvedCategory.description ||
          `Découvrez les produits DOMOELEC de la catégorie ${resolvedCategory.name} au Maroc : fiches techniques, références et demande de devis.`
        ).toString().replace(/\s+/g, ' ').trim().slice(0, 160);
      }
      // Catégorie introuvable une fois la liste chargée → noindex (cohérent avec le backend)
      if (!hasExtraFilters && Array.isArray(categories) && categories.length > 0 && !categories.some(c => c.slug === effectiveCatSlug)) {
        robots = 'noindex,follow';
      }
    } else if (familleName) {
      // Canonique PROVISOIRE auto-référencée : la décision finale (famille →
      // catégorie équivalente, indexabilité, couverture >=90 %) vient de
      // l'endpoint /api/seo/canonical ci-dessous — même source que le HTML.
      canonicalUrl = `${SITE_URL}/produits?famille=${encodeURIComponent(familleName)}`;
      leafName = familleName;
      title = `${familleName} | Produits DOMOELEC au Maroc`;
      description = `Découvrez les produits ${familleName} du catalogue DOMOELEC au Maroc : caractéristiques, références et demande de devis.`.slice(0, 160);
    }

    if (hasExtraFilters || searchValue) robots = 'noindex,follow';
    if (searchValue) {
      title = `Recherche "${searchValue}" | DOMOELEC`;
      description = `Résultats de recherche pour "${searchValue}" dans le catalogue DOMOELEC.`;
    }
    // Canonical/robots déjà présents (injectés par le serveur ou posés par la
    // décision d'une navigation précédente) : on les CONSERVE en attendant la
    // décision /api/seo/canonical — React n'écrit sa valeur provisoire que si
    // aucune balise n'existe encore (ex. hébergement statique sans SSR).
    const existingCanonicalEl = document.head.querySelector('link[rel="canonical"]');
    const existingRobotsEl = document.head.querySelector('meta[name="robots"]');
    const displayedCanonical = existingCanonicalEl?.getAttribute('href') || canonicalUrl;
    if (leafName) breadcrumb.push({ name: leafName, item: displayedCanonical });

    document.title = title;
    ensureMetaTag('name', 'description').setAttribute('content', description);
    if (!existingRobotsEl) ensureMetaTag('name', 'robots').setAttribute('content', robots);
    ensureMetaTag('property', 'og:title').setAttribute('content', title);
    ensureMetaTag('property', 'og:description').setAttribute('content', description);
    ensureMetaTag('property', 'og:url').setAttribute('content', displayedCanonical);
    ensureMetaTag('name', 'twitter:title').setAttribute('content', title);
    ensureMetaTag('name', 'twitter:description').setAttribute('content', description);

    let canonical = existingCanonicalEl;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', canonicalUrl);
      document.head.appendChild(canonical);
    }

    // Fil d'Ariane JSON-LD identique à celui injecté par backend/seo.js —
    // restaure la balise retirée lors du chargement React (cohérence
    // HTML/DOM). Uniquement sur les pages catégorie/famille, comme le serveur.
    setPageJsonLd(leafName ? [buildBreadcrumbJsonLd(breadcrumb)] : []);

    // ── Décision SEO du serveur : source unique (même calcul que le HTML
    // initial : resolveFamilleCanonical, indexabilité, catégorie canonique
    // finale, couverture >=90 %). Elle remplace les valeurs provisoires
    // ci-dessus — canonical résolue, robots réel — dès qu'elle arrive.
    // Une réponse tardive est ignorée si l'URL a changé entre-temps.
    let cancelled = false;
    const forward = {};
    searchParams.forEach((v, k) => { forward[k] = v; });
    publicProductService.getSeoCanonical(forward).then(d => {
      if (cancelled || !d || !d.canonical) return;
      // Titre/description : le serveur fournit des textes SEO par catégorie —
      // on les applique sur les pages catégorie/famille (leaf présent).
      const leaf = d.leafName || leafName;
      if (leaf && d.title && d.description) {
        document.title = d.title;
        ensureMetaTag('name', 'description').setAttribute('content', d.description);
        ensureMetaTag('property', 'og:title').setAttribute('content', d.title);
        ensureMetaTag('property', 'og:description').setAttribute('content', d.description);
        ensureMetaTag('name', 'twitter:title').setAttribute('content', d.title);
        ensureMetaTag('name', 'twitter:description').setAttribute('content', d.description);
      }
      ensureMetaTag('name', 'robots').setAttribute('content', d.robots || robots);
      ensureMetaTag('property', 'og:url').setAttribute('content', d.canonical);
      canonical.setAttribute('href', d.canonical);
      // Breadcrumb cohérent avec la canonical résolue (famille → catégorie).
      setPageJsonLd(leaf ? [buildBreadcrumbJsonLd([
        { name: 'Accueil', item: `${SITE_URL}/` },
        { name: 'Produits', item: `${SITE_URL}/produits` },
        { name: leaf, item: d.canonical }
      ])] : []);
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [searchParams, selectedCategory, categories, cascadeFamilyInfo]);
  // ═══════════════════════════════════════════════════════════════════
  // 🔑 DÉCLENCHEUR UNIQUE DE FILTRE (règle TOUS les cas, 100% fiable)
  //    - Pré-remplissage URL (category=site-imane) → CRITERE + VALEUR
  //      déjà set → lance loadData(famille + fc1 + fc1_value)
  //    - Changement CATÉGORIE N1/N2/N3 → reset CRITERE → loadData(famille)
  //    - onChange CRITERE click user OU auto Y3 (1valeur) → loadData direct
  //    - onChange VALEUR click → loadData(famille + fc1 + fc1_value)
  //    - Bouton RÉINITIALISER → famille seule
  //    DÉDUPLICATION: lastTriggeredSigRef (évite double-appel StrictMode).
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // 🔑 CORRECTION: la "famille" côté backend = facets.Famille = "Catégorie principale" (N1/racine).
    //    On utilise donc selectedCategory (N1) et non selectedLeafCategory (N2/N3) pour que le filtre
    //    famille retrouve bien les produits dont la Catégorie principale est la racine sélectionnée.
    const familleName = (selectedCategory?.name || filterCategory || '').toString().trim();
    const critereName = (filterCritere || '').toString().trim();
    const valeurName = (filterValeur || '').toString().trim();
    if (!familleName && !selectedLeafCategory?.id) {
      // Cascade entièrement effacée (× sur Catégorie / Réinitialiser) alors qu'un filtre
      // était appliqué → recharger la liste complète immédiatement, sans « Rechercher ».
      if (lastTriggeredSigRef.current !== '') {
        lastTriggeredSigRef.current = '';
        const obj = { page: 1, limit: pagination.itemsPerPage || 12, status: 'published', isVisible: true, _forceClean: true };
        if (filters?.search) obj.search = filters.search;
        loadData(obj);
      }
      return;
    }
    const limit = pagination.itemsPerPage || 12;

    const hasFcCritere = !!critereName || !!valeurName;
    const hasFcPairs = Array.isArray(selectedFcPairs) && selectedFcPairs.some(p => {
      const n = String(p?.nom || '').trim();
      if (!n) return false;
      const vs = Array.isArray(p?.valeurs) ? p.valeurs.filter(v => v != null && String(v).trim() !== '') : [];
      return vs.length > 0 || (p?.valeur != null && String(p.valeur).trim() !== '');
    });
    const anyFcActive = hasFcCritere || hasFcPairs;

    const obj = {
      page: 1,
      limit,
      status: 'published',
      isVisible: true,
    };

    if (anyFcActive) {
      obj.famille = familleName;
      if (critereName) obj.fc1_name = critereName;
      if (critereName && valeurName) obj.fc1_value = valeurName;
    }
    const backendCat = getEffectiveCategoryRef()
      || ((filters?.category && String(filters.category).trim() !== '')
        ? String(filters.category).trim()
        : '');
    if (backendCat) obj.category = backendCat;
    else if (filterCategory && !anyFcActive) obj.famille = filterCategory;

    const { params: fcParamsExtra } = buildFcParams();
    Object.assign(obj, fcParamsExtra || {});
    if (filters?.search) obj.search = filters.search;
    const FACETTE_PLATES = Object.keys(FACET_LABELS || {});
    FACETTE_PLATES.forEach(k => {
      const v = filters?.[k];
      if (v != null && String(v).trim() !== '') obj[k] = String(v).trim();
    });
    const sig = Object.keys(obj).sort().map(k => `${k}=${String(obj[k] ?? '')}`).join('|');
    if (sig === lastTriggeredSigRef.current) return;
    lastTriggeredSigRef.current = sig;
    loadDataLastSigRef.current = '';
    loadData(obj);
  }, [
    selectedLeafCategory?.name,
    selectedLeafCategory?.id,
    selectedLeafCategory?.slug,
    filterCategory,
    filterCritere,
    filterValeur,
    filters?.search,
    filters?.category,
    filters?.brand,
    filters?.categorie,
    filters?.type,
    pagination.itemsPerPage,
    selectedFcPairs.map(p => `${String(p?.nom || '')}:${(Array.isArray(p?.valeurs) ? p.valeurs.join(',') : '')}`).join('||'),
    Object.keys(FACET_LABELS || {}).map(k => `${k}=${String(filters?.[k] ?? '')}`).join('||'),
  ]);

  // 🔍 FACETTES FUSIONNÉES (API facets + AdminFilters sharedTemplates)
  // Retourne un tableau simple : [{ key, label, icon, isMultiple, options: [...] }]
  // (sans doublons, trié alphabétiquement)
  const mergedFacetsList = useMemo(() => {
    const allKeys = new Set();
    if (facets) Object.keys(facets).forEach(k => {
      if (Array.isArray(facets[k]) && facets[k].length > 0) allKeys.add(k);
    });
    if (sharedData && sharedData.sharedTemplates) {
      Object.keys(sharedData.sharedTemplates).forEach(k => {
        if (Array.isArray(sharedData.sharedTemplates[k]) && sharedData.sharedTemplates[k].length > 0) allKeys.add(k);
      });
    }
    // + current filters values (éviter cache invisible de facets)
    Object.keys(filters || {}).forEach(k => {
      if (FACET_LABELS[k] && filters[k] && String(filters[k]).trim() !== '') allKeys.add(k);
    });

    const out = [];
    allKeys.forEach(k => {
      const fromFacets = Array.isArray(facets?.[k]) ? facets[k] : [];
      const fromShared = Array.isArray(sharedData?.sharedTemplates?.[k]) ? sharedData.sharedTemplates[k] : [];
      const current = filters?.[k];
      const currentArr = FACET_LABELS[k]?.isMultiple
        ? (Array.isArray(current) ? current : [])
        : (current != null ? [String(current)] : []);

      const uniq = Array.from(new Set([
        ...fromFacets.map(v => String(v)),
        ...fromShared.map(v => String(v)),
        ...currentArr.map(v => String(v)),
      ])).filter(v => v && String(v).trim() !== '');

      if (uniq.length === 0 && currentArr.length === 0) return;
      const meta = FACET_LABELS[k] || { label: k, icon: '🔹', isMultiple: false };
      out.push({
        key: k,
        label: meta.label,
        icon: meta.icon,
        isMultiple: !!meta.isMultiple,
        options: uniq
          .sort((a, b) => a.localeCompare(b))
          .map(v => ({ value: v, label: v }))
      });
    });
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [facets, sharedData, filters]);

  const handleCategorySelect = (categorySlug) => {
    // ── DEBUG CONSOLE : trace la sélection de catégorie dans DevTools ──
    /* try { console.log('🔍 handleCategorySelect appelé avec:', categorySlug); } catch {} */
    const isAdminFiltersOrg = Boolean(typeof categorySlug === 'string'
      && categorySlug
      && !(categories.some(c => c.slug === categorySlug)));
    const category = !isAdminFiltersOrg
      ? categories.find(c => c.slug === categorySlug)
      : null;
    // Affiche si la catégorie existe en BDD ou provient de l'organigramme AdminFilters
    /* try {
      console.log('🏷️ Catégorie trouvée (BDD):', category);
      console.log('🏷️ Est organigramme AdminFilters ?', isAdminFiltersOrg);
    } catch {} */

    // ✅ Réinitialiser les filtres spécifiques précédents AVANT de changer la catégorie
    const newFilters = { ...filters };
    const oldSpecificFields = { ...categorySpecificFields };

    // Supprimer les anciens filtres spécifiques de la catégorie précédente
    Object.keys(oldSpecificFields).forEach(field => {
      delete newFilters[field];
    });

    // ✅ Supprimer aussi les filtres cat_* (groupes d'attributs précédents) ET subcategory (cascade L2/L3)
    Object.keys(newFilters).forEach((k) => {
      if (k.startsWith('cat_')) delete newFilters[k];
    });
    delete newFilters.subcategory;

    // Appliquer le nouveau filtre de catégorie
    newFilters.category = categorySlug || '';

    // ✅ Mise à jour de la catégorie sélectionnée : BDD OU organigramme AdminFilters
    if (category) {
      setSelectedCategory(category);
      if (parseSpecificFields(category.specificFields)) {
        // DEBUG CONSOLE : champs spécifiques (facettes) associés à la catégorie
        /* try { console.log('✅ Mise à jour des categorySpecificFields avec:', parseSpecificFields(category.specificFields)); } catch {} */
        setCategorySpecificFields(parseSpecificFields(category.specificFields));
      } else {
        // DEBUG CONSOLE : aucun champ spécifique → filtres facettes vidés
        /* try { console.log('❌ Pas de specificFields, réinitialisation'); } catch {} */
        setCategorySpecificFields({});
      }
    } else {
      setSelectedCategory(null);
      setCategorySpecificFields({});
    }

    // 🔁 RÉINITIALISER les niveaux enfants en cascade (L2/L3)
    setSelectedL2Id(null);
    setSelectedL3Id(null);

    // 🔁 RÉINITIALISER les filtres dynamiques de la famille
    setDynFilters({});
    setDynFamilyId(null);

    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));

    // Synchroniser l'URL
    const next = new URLSearchParams(searchParams);

    // Garder la recherche si elle existe
    if (filters.search) {
      next.set('search', filters.search);
    }

    if (categorySlug) {
      next.set('category', categorySlug);
    } else {
      next.delete('category');
    }
    next.set('page', '1');

    // Supprimer les anciens paramètres spécifiques de l'URL
    Object.keys(oldSpecificFields).forEach(field => {
      next.delete(field);
    });

    setSearchParams(next);
  };

  const handleL2Change = (valOrClear) => {
    const value = valOrClear || null;
    setSelectedL2Id(value);
    setSelectedL3Id(null);
    handleFilterChange('subcategory', value || '');
  };

  const handleL3Change = (valOrClear) => {
    const value = valOrClear || null;
    setSelectedL3Id(value);
    handleFilterChange('subcategory', value || (selectedL2Id ? String(selectedL2Id) : ''));
  };

  const handleDynFilterChange = (criterionId, nodeId) => {
    setDynFilters(prev => ({ ...prev, [criterionId]: nodeId }));
    // ⛔ PLUS de loadData(filters) direct ici:
    // (dynFilters branches legacy n'impacte pas la requête produit MySQL (pas categoryId)
    //  et l'utilisateur clique RECHERCHER si besoin ou useEffect dedup déclenchera 1 fois
  };

  const loadData = async (overrideFilters = null) => {
    try {
      const activeFilters = overrideFilters && typeof overrideFilters === 'object' ? { ...overrideFilters } : { ...filters };

      // 🧹 _forceClean : l'appelant (× sur Catégorie, Réinitialiser) demande une requête
      //    STRICTEMENT limitée aux clés de l'objet passé — ne JAMAIS réinjecter les
      //    états hérités de cette closure (filterCategory/filterCritere/selectedCategory
      //    peuvent encore contenir l'ANCIENNE valeur au moment du clic).
      const forceClean = activeFilters._forceClean === true;
      delete activeFilters._forceClean;

      const baseParams = { ...activeFilters };
      // Supprimer les champs legacy l2/l3 + cat_ ET SURTOUT subcategory qui interfère AND
      Object.keys(baseParams).forEach(k => {
        if (k.startsWith('cat_') || k.startsWith('dyn_')) delete baseParams[k];
        if (k === 'subcategory') delete baseParams[k]; // ❌ Legacy (AND avec category = ∅)
      });

      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...baseParams,
        status: 'published',
        isVisible: true,
      };
      const deepestCategoryRef = forceClean ? '' : getEffectiveCategoryRef();
      if (deepestCategoryRef) {
        params.category = deepestCategoryRef;
      }
      // ❌ SUPPRIMER pageSize (paramètre legacy UI, backend ne supporte PAS ce nom,
      // il l'interprète comme WHERE pageSize=12 → 0 produit ! Limit est déjà utilisé)
      delete params.pageSize;
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value === '' || value === undefined || value === null) { delete params[key]; return; }
        if (key === 'inStock' && value === false) { delete params[key]; return; }
        if (key === 'search' && (!value || value.trim() === '')) { delete params[key]; return; }
      });

      // ═══════════════════════════════════════════════════════════════════
      // 🔑 FILTRAGE UNIQUE FAMILLE EXCEL → CRITÈRE → VALEUR
      //    RÈGLE: La "famille" = facets.Famille = "Catégorie principale" (N1/racine).
      //           On utilise selectedCategory (N1) et non selectedLeafCategory (N2/N3).
      //           Les critères/valeurs correspondent à cette famille ONLY
      //           (pas de mélange inter-familles grâce au backend perFamille isolé).
      // ═══════════════════════════════════════════════════════════════════
      const { params: fcParams, cleanPairs: fcPairsBuildResult } = forceClean
        ? { params: {}, cleanPairs: [] }
        : buildFcParams();

      for (const key of Object.keys(fcParams || {})) {
        if (!(key in params)) {
          params[key] = fcParams[key];
        }
      }

      const cleanPairsMap = new Map();
      if (Array.isArray(fcPairsBuildResult)) {
        for (const p of fcPairsBuildResult) {
          const n = String(p?.nom || '').trim();
          if (!n) continue;
          cleanPairsMap.set(n, { nom: n, valeurs: Array.isArray(p?.valeurs) ? [...p.valeurs] : [] });
        }
      }
      for (let i = 1; i <= 50; i++) {
        const nameKey = `fc${i}_name`;
        const valueKey = `fc${i}_value`;
        const rawName = params[nameKey];
        const nameStr = String(rawName || '').trim();
        if (!nameStr) continue;
        const existing = cleanPairsMap.get(nameStr) || { nom: nameStr, valeurs: [] };
        const rawVal = params[valueKey];
        if (rawVal != null && String(rawVal).trim() !== '') {
          const vStr = String(rawVal).trim();
          if (!existing.valeurs.includes(vStr)) existing.valeurs = [...existing.valeurs, vStr];
        }
        cleanPairsMap.set(nameStr, existing);
      }
      const cleanPairs = Array.from(cleanPairsMap.values()).filter(p => String(p?.nom || '').trim());

      const familleForBackend = forceClean ? null : (selectedCategory?.name || filterCategory || null);
      const anyFcActive = (cleanPairs && cleanPairs.length > 0) || !!params.fcPairs;
      const hasBackendCategory = !!(params.category && String(params.category).trim() !== '');
      const hasBackendSubcategory = !!(params.subcategory && String(params.subcategory).trim() !== '');
      const hasCascadeExcelFilter = anyFcActive;

      if (!hasBackendCategory && !hasBackendSubcategory && familleForBackend) {
        params.famille = String(familleForBackend).trim();
      }
      // ❌ Anciennement: delete params.category DÈS que fc actif → bug pour les catégories L1 SQL + fc activés.
      //    Maintenant: SUPPRIMER category SEULEMENT SI hasBackendCategory=false (legacy Excel SITE IMANE).
      if ((hasCascadeExcelFilter && !hasBackendCategory && !hasBackendSubcategory)
          || (params.famille && !hasBackendCategory && !hasBackendSubcategory)) {
        delete params.category;
        delete params.subcategory;
      }

      const sig = Object.keys(params).sort().map(k => `${k}=${String(params[k])}`).join('&');
      if (sig === loadDataLastSigRef.current) return;
      loadDataLastSigRef.current = sig;

      // 🔑 ANTI-RACE: ID unique par appel — incrémenté APRÈS le dedup pour qu'un appel
      //    ignoré n'invalide PAS la requête identique encore en vol (sinon sa réponse
      //    serait jetée et rien ne s'afficherait).
      loadDataRequestCounterRef.current += 1;
      const thisRequestId = loadDataRequestCounterRef.current;

      setIsLoading(true);
      setError(null);

      Dbg.group('%c📡 Products.js loadData params', 'background:#0ea5e9;color:#fff;padding:2px 6px;border-radius:4px;font-weight:700');
      Dbg.log('params object:', params);
      Dbg.table(params);
      Dbg.log('query string:', new URLSearchParams(params).toString());
      Dbg.groupEnd();

      // 🔥 FIX 3/3 DÉFINTIF: Backend multi-category utilise AND (produit doit être dans TOUTES)
      //    or nous voulons UNION (produit présent dans N'IMPORTE QUELLE catégorie merged).
      //    → SI params.category contient ',' → N appels backend PARALLÈLES (un par id)
      //      → MERGE produits dédoublonnés par id + merge pagination
      let response;
      const catIds = typeof params.category === 'string' && params.category.includes(',')
        ? params.category.split(',').map(s => s.trim()).filter(Boolean)
        : null;
      if (catIds && catIds.length > 1) {
        Dbg.log(`%c🔀 MULTI-CAT PARALLÈLE (${catIds.length} ids): ${catIds.join(', ')}`, 'background:#f59e0b;color:#fff;padding:2px 6px;border-radius:4px;font-weight:700');
        const calls = catIds.map((oneId, idx) => {
          const oneParams = { ...params, category: oneId };
          Object.keys(oneParams).forEach(k => {
            const v = oneParams[k];
            if (v === '' || v === undefined || v === null) delete oneParams[k];
          });
          Dbg.log(`   call #${idx+1}: category=${oneId}`);
          return publicProductService.getProducts(oneParams).catch(err => {
            Dbg.warn(`   call #${idx+1} FAIL (cat=${oneId}):`, err.message);
            return { products: [], pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: Number(params.limit) || 12 } };
          });
        });
        const results = await Promise.all(calls);
        const mergedProducts = [];
        const seenIds = new Set();
        let totalItemsMax = 0;
        results.forEach((r, i) => {
          const prods = Array.isArray(r?.products) ? r.products : [];
          const nb = Number(r?.pagination?.totalItems) || 0;
          totalItemsMax += nb;
          Dbg.log(`   result #${i+1}: ${prods.length} produits, totalItems=${nb}`);
          prods.forEach(p => {
            if (p?.id == null) return;
            const k = String(p.id);
            if (seenIds.has(k)) return;
            seenIds.add(k);
            mergedProducts.push(p);
          });
        });
        response = {
          products: mergedProducts,
          pagination: {
            currentPage: Number(results[0]?.pagination?.currentPage) || 1,
            totalPages: Math.max(...results.map(r => Number(r?.pagination?.totalPages) || 0), 1),
            totalItems: mergedProducts.length > totalItemsMax ? mergedProducts.length : totalItemsMax,
            itemsPerPage: Number(results[0]?.pagination?.itemsPerPage) || Number(params.limit) || 12,
          },
        };
        Dbg.log(`   🔀 FINAL MERGE: ${mergedProducts.length} produits uniques / ${totalItemsMax} somme totaux`);
      } else {
        response = await publicProductService.getProducts(params);
      }

      Dbg.group('%c📦 Products.js backend response', 'background:#22c55e;color:#fff;padding:2px 6px;border-radius:4px;font-weight:700');
      Dbg.log('pagination:', response.pagination);
      Dbg.log('products.length:', response.products ? response.products.length : 'N/A');
      if (response.pagination && Number(response.pagination.totalItems) === 0) {
        Dbg.warn('⚠️ 0 produits retournés! Requête backend test direct:',
                 `http://localhost:5000/api/products/public?${new URLSearchParams(params)}`);
      }
      Dbg.groupEnd();

      // 🔑 ANTI-RACE: Si un appel PLUS RÉCENT a été lancé APRÈS celui-ci → on ignore la réponse obsolète
      //    (évite qu'un appel lent "famille 2 produits" arrive APRÈS "fc=1 produit" et écrase le bon résultat)
      const isStaleRequest = thisRequestId !== loadDataRequestCounterRef.current;
      if (isStaleRequest) {
        Dbg.warn(`⏭️ REQ OBSOLÈTE (id=${thisRequestId} < dernier=${loadDataRequestCounterRef.current}) → on ignore cette réponse.`);
        setIsLoading(false);
        return;
      }

      // 🛡️🛡️ GARDE 2: NE JAMAIS remplacer un set RICHE par un set PAUVRE (fausse alerte 0 en cascade: DÉSACTIVÉ POUR LE FILTRAGE)
      // 🔑 RÉPARATION ENCODAGE: Récupérer les accents français corrompus (monophas� → monophasé / r�seau → réseau)
      const newProducts = Array.isArray(response.products) ? response.products.map(p => deepRecover(p)) : [];
      const scoreFacets = (arr) => {
        const keys = new Set();
        arr.forEach(p => {
          const f = p?.facets;
          if (!f || typeof f !== 'object') return;
          Object.entries(f).forEach(([kk, vv]) => {
            if (!kk || typeof kk !== 'string') return;
            const nk = normalizeFacetKey(kk);
            if (!nk || FACETTES_HORS_SPEC.has(nk)) return;
            const hasVal = Array.isArray(vv) ? (vv.some(x => x != null && String(x).trim() !== ''))
                                               : (vv != null && String(vv).trim() !== '');
            if (hasVal) keys.add(nk);
          });
        });
        return keys.size;
      };
      const newScore = scoreFacets(newProducts);
      const oldScore = Number(richestProductsMetaRef.current.facetKeysSize) || 0;
      const isPageChange = overrideFilters == null && Number(params.page) !== 1;
      // 🔑 CRITÈRE CLÉ: SI ON A UN FILTRE CASCADE (fc1_name/fc1_value) → 0 MERGE, REMPLACER DIRECTEMENT
      // Évite le bug "IMANE2/TEST 2 → 2 produits encore affichés car fallback merge prev = 2.
      const hasFamilleFiltreCascade = !!(params?.famille && String(params.famille).trim() !== '');
      const hasFc1Name = !!(params?.fc1_name && String(params.fc1_name).trim() !== '');
      const hasFc1Value = !!(params?.fc1_value && String(params.fc1_value).trim() !== '');
      const hasFcPairs = !!params?.fcPairs;
      let hasAnyFcNName = false; for (const k in (params || {})) { if (/^fc\d+_name$/i.test(k) && String(params[k]).trim()) { hasAnyFcNName = true; break; } }
      const hasCascadeStrict = hasFc1Name || hasFc1Value || hasFamilleFiltreCascade || hasFcPairs || hasAnyFcNName;

      // 🔥 FIX DÉFINTIF:
      //    - PAGINATION (page>1) ET PAS DE CASCADE: merge prev + new
      //    - CASCADE (famille/fc1_name/fc1_value): 0 MERGE — remplace par newProducts
      //    - Plus de "fallback scoreFacets pauvre" qui merge
      setProducts(prev => {
        const prevArr = (prev || []).filter(p => p?.id != null);
        const newArr = (newProducts || []).filter(p => p?.id != null);

        if (hasCascadeStrict || !isPageChange) {
          // 🔄 NOUVELLE CATÉGORIE / FILTRE: REMPLACE UNIQUEMENT (0 MERGE)
          //    (fallback scoreFacets pauvre DÉSACTIVÉ complètement car il fait merge précedent
          //    2 produits famille → critère/valeur → retourne 1 produit, mais si newScore=0 (facettes mis à zéro dans une réponse nettoyée) le fallback aurait mergé les 2 produits !
          const newScoreFinal = scoreFacets(newArr);
          if (newScoreFinal > (Number(richestProductsMetaRef.current.facetKeysSize) || 0) || richestProductsMetaRef.current.facetKeysSize === -1) {
            richestProductsMetaRef.current = { facetKeysSize: newScoreFinal, idsHash: '' };
          }

          return newArr.slice();
        } else if (isPageChange) {
          // ➕ PAGINATION: Fusionner prev + new sans doublons
          const map = new Map();
          prevArr.forEach(p => map.set(String(p.id), p));
          newArr.forEach(p => map.set(String(p.id), p));
          const merged = Array.from(map.values());
          const mergedScore = scoreFacets(merged);
          if (mergedScore > (Number(richestProductsMetaRef.current.facetKeysSize) || 0) || richestProductsMetaRef.current.facetKeysSize === -1) {
            richestProductsMetaRef.current = { facetKeysSize: mergedScore, idsHash: '' };
          }

          return merged;
        }
        return newArr.slice();
      });
      if (newScore > oldScore || richestProductsMetaRef.current.facetKeysSize === -1) {
        richestProductsMetaRef.current = { facetKeysSize: newScore, idsHash: '' };
      }
      setPagination(response.pagination);

      // ✅ FUSION FACILE (évite 8s attente polling): agrège immédiatement facets[] depuis produits qu'on vient de charger
      if (Array.isArray(response.products)) {
        const aggregated = { };
        response.products.forEach(p => {
          // 🔒 FIX 3a: Utiliser safeParseFacetsObj (JSON.parse fallback string → object)
          //    pour ne rater AUCUNE facette même si backend serialise en string
          const obj = safeParseFacetsObj(p);
          if (!obj) return;
          Object.entries(obj).forEach(([k, rawV]) => {
            if (!k || typeof k !== 'string') return;
            const values = Array.isArray(rawV) ? rawV.filter(v => v != null && String(v).trim() !== '')
                             : ((rawV != null && String(rawV).trim() !== '') ? [String(rawV).trim()] : []);
            if (values.length === 0) return;
            if (!aggregated[k]) aggregated[k] = new Set();
            values.forEach(v => { const rv = recoverFrenchText(String(v).trim()); if (rv) aggregated[k].add(rv); });
          });
        });
        const objectified = { };
        Object.entries(aggregated).forEach(([k, s]) => { objectified[k] = Array.from(s); });
        const objKeys = Object.keys(objectified);
        if (objKeys.length > 0) {
          setFacets(prev => {
            const next = { ...(prev || {}) };
            let changed = false;
            Object.entries(objectified).forEach(([k, arr]) => {
              const prevA = Array.isArray(next[k]) ? next[k] : [];
              const merged = Array.from(new Set([...prevA, ...arr]));
              if (merged.length !== prevA.length || prevA.some((v,i) => v !== merged[i])) {
                next[k] = merged; changed = true;
              }
            });
            // 🔒 FIX 3b: SI AUCUN CHANGEMENT → return prev (évite setFilters inutile + skipNextFilterEffectRef.use)
            if (!changed) return prev;
            // Init filters[k] = '' pour LES NOUVELLES clés seulement (si nouvelles)
            setFilters(prevF => {
              const up = { ...prevF };
              let c = false;
              objKeys.forEach(k => { if (up[k] === undefined) { up[k] = ''; c = true; } });
              return c ? up : prevF;
            });
            return next;
          });
        }
      }
    } catch (error) {
      console.error('❌ Products.js - Erreur lors du chargement des données:', error);
      if (DEBUG_MODE) {
        console.error('❌ Products.js - Détails de l\'erreur:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: error.config
        });
      }
      setError(`Erreur lors du chargement des produits: ${error.message}`);
    } finally {
      hasEverLoadedRef.current = true;
      setIsLoading(false);
    }
  };

  // ── DEBUG GLOBAL (DevTools Console) ──
  try {
    window.__DOMOELEC = {
      ...(window.__DOMOELEC || {}),
      testCategory: (id) => { loadDataLastSigRef.current = ''; loadData({ category: Number(id), page:1, limit:12, status:'published', isVisible:true }); },
      loadRaw: (obj) => { loadDataLastSigRef.current = ''; loadData(obj); },
      getFiltersSnapshot: () => ({ ...filters }),
      getCascadePath: () => [...cascadePath],
    };
  } catch { /* SSR ignore */ }

  const handleFilterChange = (key, value) => {
    // Mettre à jour les filtres immédiatement
    setFilters(prev => {
      const updated = { ...prev, [key]: value };
      if (key === 'category' && !value) {
        Object.keys(categorySpecificFields || {}).forEach(field => {
          delete updated[field];
        });
        Object.keys(updated).forEach(k => {
          if (k.startsWith('cat_')) delete updated[k];
        });
      }
      return updated;
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));

    const next = new URLSearchParams(searchParams);
    const updatedFilters = { ...filters, [key]: value };

    const reservedSync = ['search', 'category', 'sortBy', 'sortOrder', 'pageSize'];
    const facetAndCatKeys = Object.keys(updatedFilters).filter(k =>
      !KNOWN_RESERVED_URL_PARAMS.has(k) && k !== 'page'
    );
    const keysToSync = Array.from(new Set([...reservedSync, ...facetAndCatKeys]));

    keysToSync.forEach(k => {
      const v = updatedFilters[k];
      if (v !== undefined && v !== null && v !== '' && String(v).trim() !== '' && v !== false) {
        next.set(k, String(v));
      } else {
        next.delete(k);
      }
    });
    next.set('page', '1');

    setSearchParams(next);
  };

  const normalizeImageUrl = (raw) => {
    if (!raw) return '';
    let url = String(raw).trim();

    const extractDriveId = (u) => {
      if (!u || typeof u !== 'string') return null;
      const rawTrim = u.trim();
      if (rawTrim.startsWith('/api/image-proxy')) return null;
      const patterns = [
        /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
        /[?&]id=([a-zA-Z0-9_-]+)/,
        /uc\?export=view&id=([a-zA-Z0-9_-]+)/,
        /thumbnail\?id=([a-zA-Z0-9_-]+)/,
        /lh3\.googleusercontent\.com\/d\/([a-zA-Z0-9_-]+)/,
        /^([a-zA-Z0-9_-]{25,})$/
      ];
      for (const p of patterns) {
        const m = rawTrim.match(p);
        if (m && m[1]) return m[1];
      }
      return null;
    };
    const buildProxyUrl = (fileId, sizeHint = 'w1600-h1600') => {
      if (!fileId) return '';
      return `/api/image-proxy?id=${encodeURIComponent(fileId)}&sz=${encodeURIComponent(sizeHint)}`;
    };

    const id = extractDriveId(url);
    if (id) {
      return buildProxyUrl(id, 'w1600-h1600');
    }

    if (/^https?:\/\//i.test(url)) return url;
    url = url.replace(/\\/g, '/');
    if (!url.startsWith('/')) url = `/${url}`;
    return `${url}`;
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const getCategoryName = (categoryIdOrSlug) => {
    if (!categoryIdOrSlug) return 'Non catégorisé';
    
    let category = categories.find(cat => cat.slug === categoryIdOrSlug);
    if (!category) {
      category = categories.find(cat => cat._id === categoryIdOrSlug);
    }
    
    return category ? category.name : 'Non catégorisé';
  };

  // Compter les filtres actifs — EXCLUT pageSize, inStock=false et les réservés qui gardent une valeur par défaut non-filtre
  const getActiveFiltersCount = () => {
    let count = Object.entries(filters).filter(([k,v]) => {
      if (['search','category','sortBy','sortOrder','page','pageSize','limit','status','isVisible','famille','fcPairs'].includes(k)) return false;
      if (/^fc\d+_(name|value)$/i.test(k)) return false;
      if (k === 'inStock' && v === false) return false;
      if (v === undefined || v === null) return false;
      if (typeof v === 'boolean' && v === false) return false;
      if (Array.isArray(v) && v.length === 0) return false;
      if (typeof v === 'string' && v.trim() === '') return false;
      return true;
    }).length;
    if (filterCategory) count++;
    if (filterCritere) count++;
    if (filterValeur) count++;
    count += (selectedFcPairs || []).reduce((acc, p) => acc + ((Array.isArray(p?.valeurs) ? p.valeurs : []).filter(v => String(v || '').trim() !== '').length > 0 ? 1 : 0), 0);
    return count;
  };

  // Réinitialiser tous les filtres (DYNAMIQUE)
  const resetAllFilters = () => {
    const resetFilters = { ...filters };

    // Réinitialiser TOUS les champs facets et cat_* (quel que soit leur nom)
    Object.keys(resetFilters).forEach(k => {
      if (KNOWN_RESERVED_URL_PARAMS.has(k)) return;
      if (k === 'page' || k === 'pageSize') return;
      resetFilters[k] = k === 'inStock' ? false : '';
    });

    // Réinitialiser la cascade L2/L3 et les filtres dynamiques
    setCascadePath([]);
    setSelectedCategory(null);
    setSelectedL2Id(null);
    setSelectedL3Id(null);
    setDynFilters({});
    setDynFamilyId(null);

    // 🔑 NOUVEAU: reset filtre unique (catégorie → critère → valeur)
    resetFilterAll();

    setFilters(resetFilters);
    setFiltersDrawerOpen(false);
    setMobileFiltersOpen(false);

    const next = new URLSearchParams();
    if (filters.search) next.set('search', filters.search);
    next.set('page', '1');
    next.set('pageSize', String(pagination.itemsPerPage || 12));
    setSearchParams(next);

    // 🔑 SPEC: Charger liste complète (page 1, sans CRITERE / VALEUR)
    //    _forceClean : cette closure contient encore les ANCIENS états — sans lui,
    //    l'ancien critère/valeur serait réinjecté dans la requête "réinitialisée".
    try {
      loadDataLastSigRef.current = '';
      const obj = {
        page: 1,
        limit: pagination.itemsPerPage || 12,
        status: 'published',
        isVisible: true,
        _forceClean: true,
      };
      if (filters?.search) obj.search = filters.search;
      loadData(obj);
    } catch (_) {}
  };

// Rendu des filtres avancés — helper stable pour ne pas perdre le focus a la frappe
  const renderAdvancedFilters = (isMobile = false) => {
    const activeCount = getActiveFiltersCount();
    // Rendu dans document.body : évite listes coupées (sidebar sticky / drawer mobile)
    const filterPopupContainer = () => document.body;

    return (
      <div className="pf-panel">
        {/* En-tête */}
        <div className="pf-header">
          <div className="pf-header__title">
            <span className="pf-header__icon"><FilterOutlined /></span>
            <span className="pf-header__label">Filtres</span>
            {activeCount > 0 && <span className="pf-header__badge">{activeCount}</span>}
          </div>
          {activeCount > 0 && (
            <button type="button" className="pf-header__reset" onClick={resetAllFilters}>
              <ClearOutlined /> Réinitialiser
            </button>
          )}
        </div>

        {SHOW_SIDEBAR_SEARCH_AND_CATEGORY && (
          <>
  {/* Champ de recherche avec suggestions */}
<div className="pf-field">
  <label className="pf-label">Recherche</label>

  <AutoComplete
    className="pf-search"
    value={query}
    options={suggestions}
    filterOption={false}
    onChange={(val) => setQuery(val)}
    onSelect={(val, option) => {
      setQuery(val);

      if (option?.slug) {
        navigate(`/produit/${option.slug}`);
      }
    }}
    onKeyDown={(event) => {
      if (event.key === "Enter") {
        handleSearch();
      }
    }}
    open={query.trim().length > 0 && suggestions.length > 0}
    getPopupContainer={filterPopupContainer}
    style={{ width: "100%" }}
    popupMatchSelectWidth
    allowClear
    size="large"
    placeholder="Nom, référence..."
    prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
  />
</div>

        {/* =========================================================== */}
        {/* 🔑 CATÉGORIES (NIVEAU 1 → 2 → 3) — ORIGINAL, design INTACT  */}
        {/*    Source: CATEGORY_TREE_MERGED / BACKEND_TREE (publicCategoryService.getCategories) */}
        {/*    ❌ familyTree NE sert PAS à lister les catégories          */}
        {/* =========================================================== */}

        {/* =========================================================== */}
        {/* 🔑 CASCADE DYNAMIQUE : CATÉGORIE → NOM DE FILTRE → VALEUR     */}
        {/*    Source: cascadeFamilyInfo.familyTree (GET /public/facets)  */}
        {/*    calculé sur TOUS les produits publiés de la catégorie —    */}
        {/*    aucun nom de filtre prédéfini dans le code.                */}
        {/* =========================================================== */}

        {/* NIVEAU 1 : CATÉGORIE (catégorie principale des produits publiés) */}
        <div className="pf-field">
          <label className="pf-label">Catégorie</label>
          <AntSelect
            size="large"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Choisir une catégorie"
            value={filterCategory ? String(filterCategory) : undefined}
            onChange={handleDynamicCategoryChange}
            options={dynamicCategoryOptions}
            placement="bottomLeft"
            getPopupContainer={filterPopupContainer}
            style={{ width: '100%' }}
            popupMatchSelectWidth={true}
            listHeight={280}
            className="pf-categorie-select"
          />
        </div>

        {/* FILTRES DYNAMIQUES CUMULABLES : un select multi-valeurs par critère
            (Marque toujours + critères multi-valeurs de la catégorie).
            ET entre critères, OU entre valeurs d'un même critère. */}
        {filterCategory && dynamicCriteriaGroups.map(group => (
          <div className="pf-field" key={group.nom}>
            <label className="pf-label">{group.label}</label>
            <AntSelect
              size="large"
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={`Choisir : ${group.label}`}
              maxTagCount={2}
              value={getPairValeurs(group.nom)}
              onChange={(vals) => handleCriterionValuesChange(group.nom, vals)}
              options={group.options}
              placement="bottomLeft"
              getPopupContainer={filterPopupContainer}
              style={{ width: '100%' }}
              popupMatchSelectWidth={true}
              listHeight={280}
              className="pf-categorie-select"
            />
          </div>
        ))}
          </>
        )}

        {/* Actions */}
        <div className="pf-actions">
          <button type="button" className="pf-btn-search" onClick={handleSearch}>
            <SearchOutlined />
            <span>Rechercher</span>
          </button>

          {activeCount > 0 && (
            <button type="button" className="pf-btn-reset" onClick={resetAllFilters}>
              <ClearOutlined /> Effacer tous les filtres
            </button>
          )}
        </div>
      </div>
    );
  };

  // 🔑 SIMPLIFIÉ: Le backend filtre DÉJÀ correctement via params famille/fc1_name/fc1_value.
  //    Pas de double filtrage frontend nécessaire — on fait confiance au résultat du backend.
  const displayProducts = useMemo(() => {
    return products || [];
  }, [products]);

  // 🔧 TOTAL PRODUITS: On fait confiance au backend (pagination.totalItems) car le WHERE
  //    famille + fcPairs est maintenant exécuté côté MySQL.
  const hasCascadeFilterActive = !!(filterCategory || filterCritere || filterValeur);
  const hasActiveFrontendFilters = false;

  const totalProductsForDisplay = useMemo(() => {
    if (hasActiveFrontendFilters) {
      return (displayProducts || []).length;
    }
    const backendTotal = Number(pagination?.totalItems || 0);
    const loadedLength = (displayProducts || []).length;
    return Math.max(backendTotal, loadedLength);
  }, [hasActiveFrontendFilters, displayProducts, pagination?.totalItems]);

  // Spinner pleine page UNIQUEMENT au tout premier chargement.
  //  Ensuite: la page reste montée (selects conservés) et un overlay léger
  //  couvre la zone résultats pendant la requête — liste et compteur
  //  se mettent à jour ensemble dans le même commit.
  if (isLoading && !hasEverLoadedRef.current) {
    return (
      <div className="products-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Chargement des produits...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="container">
        <div style={{ padding: '24px 0' }}>
          {filters.search && filters.search.trim() !== '' ? (
            <div>
              <Title level={1} style={{ marginBottom: 4, fontSize: 20, lineHeight: '28px' }}>
                Résultats de recherche pour "{filters.search}"
              </Title>
              <Text type="secondary">
                {pagination.totalItems} produit{pagination.totalItems > 1 ? 's' : ''} trouvé{pagination.totalItems > 1 ? 's' : ''}
              </Text>
              <div style={{ marginTop: 8 }}>
                <AntButton 
                  type="link" 
                  onClick={() => {
                    setQuery('');
                    setFilters(prev => ({ ...prev, search: '' }));
                  }}
                >
                  Voir tous les produits
                </AntButton>
              </div>
            </div>
          ) : (
            <div>
              {(() => {
                // Titre adapté à la page : « Vidéosurveillance au Maroc » pour
                // une catégorie/famille sélectionnée, catalogue général sinon.
                const urlCatSlug = String(searchParams.get('category') || '').trim();
                const urlCategory = urlCatSlug
                  ? (Array.isArray(categories) ? categories : []).find(c => c && String(c.slug || '').trim() === urlCatSlug)
                  : null;
                const headerName =
                  selectedLeafCategory?.name ||
                  selectedCategory?.name ||
                  urlCategory?.name ||
                  String(searchParams.get('famille') || '').trim();
                const headerDesc =
                  (selectedLeafCategory?.description || selectedCategory?.description || urlCategory?.description || '').toString().replace(/\s+/g, ' ').trim();
                return (
                  <>
                    <Title level={1} style={{ marginBottom: 4, fontSize: 20, lineHeight: '28px' }}>
                      {headerName ? `${toDisplayName(headerName)} au Maroc` : 'Nos produits'}
                    </Title>
                    <Text type="secondary">
                      {headerName
                        ? (headerDesc || `Découvrez notre gamme ${toDisplayName(headerName)} en courant faible`)
                        : 'Découvrez notre gamme complète de solutions en courant faible'}
                      {(() => {
                        const total = totalProductsForDisplay;
                        if (total <= 0) return null;
                        return (
                          <span> - {total} produit{total > 1 ? 's' : ''} disponible{total > 1 ? 's' : ''}</span>
                        );
                      })()}
                    </Text>
                  </>
                );
              })()}
            </div>
          )}
        </div>
        {/* 🟢 Bouton Filtres visible uniquement sur mobile (sous le header) */}
        <div className="mobile-filters-bar">
          <AntButton
            icon={<FilterOutlined />}
            onClick={() => setFiltersDrawerOpen(true)}
            type="default"
          >
            Filtres ({getActiveFiltersCount()})
          </AntButton>
        </div>

        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i>
            {error}
          </div>
        )}

         <section className="results-area" style={{ display: 'flex', gap: 24 }}>
  {/* ✅ Colonne gauche - Filtres */}
          <aside style={{ width: 260, flexShrink: 0 }}>
    {renderAdvancedFilters()}
  </aside>

  {/* ✅ Colonne droite - Résultats produits */}
  <div style={{ flex: 1, position: 'relative' }} aria-busy={isLoading ? 'true' : 'false'}>
    <div ref={resultsTopRef} className="products-results-anchor" />

    {/* État de chargement pendant la requête (page conservée, liste+compteur mis à jour ensemble) */}
    {isLoading && (
      <div className="pp-loading-overlay" role="status" aria-live="polite">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Chargement des produits...</span>
      </div>
    )}


    {/* Résultats */}
    <Title level={5} style={{ marginBottom: 24 }}>
      {filters.search 
        ? `Résultats pour "${filters.search}"`
        : 'Tous les produits'}
      <Text type="secondary" style={{ marginLeft: 8 }}>
        ({totalProductsForDisplay} produit{totalProductsForDisplay > 1 ? 's' : ''})
      </Text>
    </Title>

    {displayProducts.length === 0 ? (
      <div className="no-products">
        <i className="fas fa-box-open"></i>
        <h3>Aucun produit trouvé</h3>
        <p>Essayez de modifier vos critères de recherche</p>
      </div>
    ) : (
      <div className="pp-grid">
        {displayProducts.map(product => {
          const imgs = typeof product.images === 'string'
            ? (() => { try { return JSON.parse(product.images); } catch(_) { return {}; } })()
            : (product.images || {});
          const raw = imgs.main || product.imageUrl;
          const extractInlineId = (u) => {
            if (!u) return null;
            const s = String(u).trim();
            if (s.startsWith('/api/image-proxy')) return null;
            const patterns = [
              /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
              /[?&]id=([a-zA-Z0-9_-]+)/,
              /lh3\.googleusercontent\.com\/d\/([a-zA-Z0-9_-]+)/,
              /^([a-zA-Z0-9_-]{25,})$/
            ];
            for (const p of patterns) {
              const m = s.match(p);
              if (m && m[1]) return m[1];
            }
            return null;
          };
          const fileId = extractInlineId(raw);
          const proxyUrl = (id, sz) => `/api/image-proxy?id=${encodeURIComponent(id)}&sz=${encodeURIComponent(sz)}`;
          const imgSrc = fileId
            ? proxyUrl(fileId, 'w400-h400')
            : normalizeImageUrl(raw);
          const categoryName = product.category?.name || product.categoryName;

          return (
            <Link
              to={`/produit/${product.slug}`}
              key={product.id ?? product._id ?? `prod-${product.slug ?? product.reference ?? String(Math.random()).slice(2)}`}
              className="pp-card"
            >
              <div className="pp-card__media">
                {categoryName && <span className="pp-card__tag">{categoryName}</span>}
                <img
                  src={imgSrc}
                  alt={cleanDisplay(product.name)}
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    if (fileId && !e.currentTarget.dataset.retry) {
                      e.currentTarget.dataset.retry = '1';
                      e.currentTarget.src = proxyUrl(fileId, 'w800');
                    } else {
                      e.currentTarget.style.visibility = 'hidden';
                    }
                  }}
                />
              </div>
              <div className="pp-card__body">
                <h3 className="pp-card__title">{cleanDisplay(product.name)}</h3>
                <p className="pp-card__ref">
                  <i className="fas fa-hashtag"></i>
                  <span>{product.reference}</span>
                </p>
                <div className="pp-card__cta">
                  <span>Voir le produit</span>
                  <i className="fas fa-arrow-right"></i>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    )}

    {/* Pagination simple */}
    {pagination.totalPages > 1 && (
      <SimplePagination
        current={pagination.currentPage}
        total={pagination.totalItems}
        pageSize={pagination.itemsPerPage}
        onChange={(page) => {
          if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
          }
          shouldScrollAfterPaginationRef.current = true;
          const next = new URLSearchParams(searchParams);
          next.set('page', String(page));
          next.set('pageSize', String(pagination.itemsPerPage));
          setSearchParams(next);
          setPagination(prev => ({ ...prev, currentPage: page }));
        }}
        onShowSizeChange={(current, size) => {
          if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
          }
          shouldScrollAfterPaginationRef.current = true;
          const next = new URLSearchParams(searchParams);
          next.set('page', '1');
          next.set('pageSize', String(size));
          setSearchParams(next);
          setPagination(prev => ({
            ...prev,
            currentPage: 1,
            itemsPerPage: size
          }));
        }}
        showSizeChanger={true}
        showQuickJumper={false}
        showTotal={true}
        size="default"
      />
    )}
  </div>
</section>

      </div>

      {/* 🟢 Drawer mobile pour filtres */}
      <Drawer
        title="Filtres produits"
        placement="left"
        onClose={() => setFiltersDrawerOpen(false)}
        open={filtersDrawerOpen}
        size="85%"
        styles={{ body: { paddingBottom: 80 } }}
      >
        {renderAdvancedFilters(true)}
      </Drawer>
    </div>
  );
};

export default Products; 
